/*
 * Savile Standard - Virtual fitting room
 * Vanilla JS, no dependencies, no HTML strings: every node is built with the DOM API.
 *
 * How it works
 * 1. Body estimate: height, weight and age give an estimated chest, waist and hip (cm).
 *    The shopper can then set each one in cm or inches (shape step and My measurements).
 * 2. Size table: each size has a body range per area (e.g. M chest 94-100).
 *    Each area is rated against the range; the size with the lowest penalty wins.
 * 3. Figure: a shop mannequin rendered in 3D (WebGL ray marching) from those numbers,
 *    turned three-quarters, so it widens and narrows at the chest, waist and hip.
 *
 * PLACEHOLDER NOTICE: the estimate formulas and the default table are generic menswear
 * averages. Replace the table with the real measurements once the supplier sample arrives.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'ss-fit-profile-v2';
  var OLD_STORAGE_KEY = 'ss-fit-profile-v1';
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Tolerances in cm: how far the body may sit outside a size's range. */
  var RULES = { okTight: 2.5, warnTight: 6, okLoose: 3, warnLoose: 7 };
  /* Shape step: sliders run this many cm either side of the estimate. My measurements: absolute limits. */
  var SHAPE_RANGE = 16;
  var MEAS_LIMITS = { chest: [70, 160], waist: [55, 160], hip: [70, 160], arm: [45, 85], inseam: [60, 100], wrist: [12, 25], neck: [30, 55] };
  /* Shape step: two steps either way; one step is worth this many cm (also used to read old profiles). */
  var OLD_STEP = { chest: 4, waist: 5, hip: 4 };
  /* one step of the simple +/- controls, in cm (inseam: leg length for bottoms) */
  var STEP_CM = { chest: 4, waist: 5, hip: 4, inseam: 3 };
  /* Bottoms: the waist is taken at the belt line (cm above the natural waist reading, and where
     the tape sits on the figure, in body-height units) */
  var BELT_OFFSET = 3;
  var BELT_Y = 0.585;
  /* Length measurements read as short / long instead of tight / loose */
  var LENGTH_KEYS = { slightly_tight: 'slightly_short', tight: 'short', too_tight: 'too_short', slightly_loose: 'slightly_long', loose: 'long', too_loose: 'too_long' };
  var LENGTH_AREAS = { inseam: true, arm: true };
  var LIMITS = { height: [140, 215], weight: [40, 180], age: [15, 90] };
  var AREA_ORDER = ['chest', 'waist', 'hip', 'neck', 'arm', 'inseam', 'wrist'];
  var AREA_ALIASES = {
    chest: ['chest', 'bust', 'torax', 't\u00f3rax', 'peito', 'poitrine', 'pecho', 'brust', 'brustumfang'],
    waist: ['waist', 'cintura', 'tour de taille', 'taillenumfang', 'bund'],
    hip: ['hip', 'hips', 'quadril', 'hanche', 'hanches', 'cadera', 'h\u00fcfte', 'huefte', 'ges\u00e4\u00df'],
    neck: ['neck', 'collar', 'pesco\u00e7o', 'pescoco', 'gola', 'tour de cou', 'cuello', 'hals', 'kragen'],
    inseam: ['inseam', 'inside leg', 'entrepierna', 'entrejambe', 'entreperna', 'schrittl\u00e4nge', 'schritt'],
    arm: ['arm', 'sleeve', 'manga', 'bra\u00e7o', 'braco', 'bras', 'brazo', '\u00e4rmel', 'aermel'],
    wrist: ['wrist', 'fist', 'punho', 'pulso', 'poignet', 'mu\u00f1eca', 'handgelenk']
  };

  function qs(selector, root) { return (root || document).querySelector(selector); }
  function qsa(selector, root) { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); }
  function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function fmt(v) { return (Math.round(v * 10) / 10).toFixed(1).replace(/\.0$/, ''); }
  function fill(template, map) {
    return String(template).replace(/\[(\w+)\]/g, function (m, key) { return key in map ? map[key] : m; });
  }
  function svg(tag, attrs, parent) {
    var el = document.createElementNS(SVG_NS, tag);
    if (attrs) Object.keys(attrs).forEach(function (k) { el.setAttribute(k, attrs[k]); });
    if (parent) parent.appendChild(el);
    return el;
  }
  function storageGet() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
      var old = window.localStorage.getItem(OLD_STORAGE_KEY);
      if (!old) return null;
      var v1 = JSON.parse(old); var shape = v1.shape || {};
      v1.adjust = { chest: (shape.chest || 0) * OLD_STEP.chest, waist: (shape.waist || 0) * OLD_STEP.waist, hip: (shape.hip || 0) * OLD_STEP.hip };
      delete v1.shape;
      return v1;
    } catch (e) { return null; }
  }
  function storageSet(value) {
    try {
      if (value) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* private mode: the tool still works, it just won't remember */ }
  }
  function parseNumber(value) {
    if (value === null || value === undefined) return NaN;
    var clean = String(value).trim().replace(',', '.');
    if (!/^\d+(\.\d+)?$/.test(clean)) return NaN;
    return parseFloat(clean);
  }

  /* ---------- Size table ---------- */

  function matchArea(label) {
    var text = String(label || '').toLowerCase().trim();
    var found = null;
    AREA_ORDER.forEach(function (area) {
      if (found) return;
      AREA_ALIASES[area].forEach(function (alias) { if (!found && text.indexOf(alias) !== -1) found = area; });
    });
    return found;
  }

  function parseRange(cell) {
    var text = String(cell || '').replace(/,/g, '.');
    var m = text.match(/(\d+(?:\.\d+)?)\s*(?:-|\u2013|\u2014|to|a|bis|\u00e0)\s*(\d+(?:\.\d+)?)/i);
    if (m) {
      var a = parseFloat(m[1]); var b = parseFloat(m[2]);
      return [Math.min(a, b), Math.max(a, b)];
    }
    var single = text.match(/(\d+(?:\.\d+)?)/);
    if (single) { var v = parseFloat(single[1]); return [v - 2, v + 2]; }
    return null;
  }

  function parseTable(text) {
    var columns = ['chest', 'waist'];
    var sizes = [];
    String(text || '').split(/\r?\n/).forEach(function (raw) {
      var line = raw.trim();
      if (!line || line.charAt(0) === '#') return;
      var cells = line.split(/\s*[|;\t]\s*/).map(function (c) { return c.trim(); });
      if (cells.length < 2) return;
      var hasNumbers = cells.slice(1).some(function (c) { return /\d/.test(c); });
      if (!hasNumbers) { columns = cells.slice(1).map(matchArea); return; }
      var ranges = {};
      cells.slice(1).forEach(function (cell, i) {
        var area = columns[i];
        var range = area ? parseRange(cell) : null;
        if (range) ranges[area] = range;
      });
      if (cells[0] && Object.keys(ranges).length) sizes.push({ name: cells[0], ranges: ranges });
    });
    var areas = AREA_ORDER.filter(function (area) {
      return sizes.some(function (s) { return s.ranges[area]; });
    });
    return { areas: areas, sizes: sizes };
  }

  /* ---------- Body estimate (placeholder model) ---------- */

  function bmiOf(profile) {
    var m = profile.height / 100;
    return profile.weight / (m * m);
  }

  function estimate(profile) {
    var b = clamp(bmiOf(profile), 15, 45);
    var h = profile.height - 178;
    var a = clamp(profile.age, 15, 90) - 35;
    var adjust = profile.adjust || {};
    return {
      chest: 100 + (b - 24.6) * 2.6 + h * 0.15 + a * 0.04 + (adjust.chest || 0),
      waist: 88 + (b - 24.6) * 3.0 + h * 0.10 + a * 0.18 + (adjust.waist || 0),
      hip: 100 + (b - 24.6) * 2.1 + h * 0.20 + a * 0.03 + (adjust.hip || 0),
      neck: 38.5 + (b - 24.6) * 0.55 + h * 0.03 + a * 0.02 + (adjust.neck || 0),
      arm: profile.height * 0.352 + (adjust.arm || 0),
      inseam: profile.height * 0.45 + (adjust.inseam || 0),
      wrist: 17 + (b - 24.6) * 0.22 + h * 0.02 + (adjust.wrist || 0)
    };
  }

  function rateArea(body, range) {
    var min = range[0]; var max = range[1];
    if (body > max) {
      var over = body - max;
      if (over <= RULES.okTight) return { key: 'slightly_tight', level: 'ok', penalty: 0.6 + over * 0.25 };
      if (over <= RULES.warnTight) return { key: 'tight', level: 'warn', penalty: 2 + over * 0.5 };
      return { key: 'too_tight', level: 'bad', penalty: 5 + over };
    }
    if (body < min) {
      var under = min - body;
      if (under <= RULES.okLoose) return { key: 'slightly_loose', level: 'ok', penalty: 0.5 + under * 0.2 };
      if (under <= RULES.warnLoose) return { key: 'loose', level: 'warn', penalty: 1.8 + under * 0.35 };
      return { key: 'too_loose', level: 'bad', penalty: 4 + under * 0.8 };
    }
    var mid = (min + max) / 2;
    var half = Math.max(0.5, (max - min) / 2);
    return { key: 'ideal', level: 'ok', penalty: Math.abs(body - mid) / half * 0.4 };
  }

  /* Score for a length (inseam, sleeve). Lengths can be altered, so they nudge the choice
     rather than outweigh the girths: a smooth penalty that rises steadily outside the range,
     more when the garment would be too short (it can't be let down) than too long (it can be
     taken up). Because it only ever bends upwards (no steps, no cap), a longer leg can never
     tip the choice towards a smaller size. The per-area label still comes from rateArea. */
  function lengthPenalty(body, range) {
    var min = range[0]; var max = range[1];
    var mid = (min + max) / 2; var edge = (max - min) / 2 * 0.15;
    if (body > max) return edge + (body - max) * 0.45;
    if (body < min) return edge + (min - body) * 0.3;
    return Math.abs(body - mid) * 0.15;
  }

  var LEVEL_RANK = { ok: 0, warn: 1, bad: 2 };

  function recommend(body, table) {
    var results = table.sizes.map(function (size) {
      var areas = {}; var score = 0; var level = 'ok';
      table.areas.forEach(function (area) {
        if (!size.ranges[area]) return;
        var r = rateArea(body[area], size.ranges[area]);
        areas[area] = r;
        score += LENGTH_AREAS[area] ? lengthPenalty(body[area], size.ranges[area]) : r.penalty;
        if (LEVEL_RANK[r.level] > LEVEL_RANK[level]) level = r.level;
      });
      return { size: size, areas: areas, score: score, level: level };
    });
    var best = 0;
    results.forEach(function (r, i) { if (r.score < results[best].score) best = i; });
    var outOfRange = results.length > 0 && results.every(function (r) { return r.level === 'bad'; });
    /* Display only: the recommended size is shown as a clean "Ideal fit" whenever none of its
       areas is 'bad'. Scores, levels and per-area keys stay untouched for any other use. */
    var isTopPick = results.length > 0 && results[best].level !== 'bad';
    results.forEach(function (r, i) { r.isTopPick = i === best && isTopPick; });
    return { results: results, best: best, outOfRange: outOfRange, isTopPick: isTopPick };
  }

  /* ---------- Figure: a 3D shop mannequin, ray marched in WebGL ----------
     The body is a set of soft shapes (ellipsoids, rounded cones, capsules) blended
     together, so head, neck, torso, arms and hands read as one smooth piece.
     All sizes are in units of body height. */

  var FRAG = [
    'precision highp float;',
    'uniform vec2 uRes; uniform vec4 uView; uniform mat3 uM; uniform vec3 uSkin; uniform float uCm;',
    'uniform vec4 uChest; uniform vec4 uWaist; uniform vec4 uHip; uniform vec4 uLimb; uniform vec4 uArm;',
    'uniform vec4 uBandG[4]; uniform vec4 uBandC[4]; uniform vec4 uWrist; uniform float uAA; uniform float uQ;',
    'float smin(float a, float b, float k){ float h = max(k - abs(a - b), 0.0) / k; return min(a, b) - h * h * k * 0.25; }',
    'float ssub(float cut, float d, float k){ float h = clamp(0.5 - 0.5 * (d + cut) / k, 0.0, 1.0); return mix(d, -cut, h) + k * h * (1.0 - h); }',
    'float sminC(float a, float b, float k){ float h = max(k - abs(a - b), 0.0) / k; return min(a, b) - h * h * h * k * (1.0 / 6.0); }',
    'float sdE(vec3 p, vec3 r){ float k0 = length(p / r); float k1 = length(p / (r * r)); return k0 * (k0 - 1.0) / k1; }',
    'float sdRC(vec3 p, vec3 a, vec3 b, float r1, float r2){',
    '  vec3 ba = b - a; float l2 = dot(ba, ba); float rr = r1 - r2; float a2 = l2 - rr * rr; float il2 = 1.0 / l2;',
    '  vec3 pa = p - a; float y = dot(pa, ba); float z = y - l2; vec3 xv = pa * l2 - ba * y; float x2 = dot(xv, xv);',
    '  float y2 = y * y * l2; float z2 = z * z * l2; float k = sign(rr) * rr * rr * x2;',
    '  if (sign(z) * a2 * z2 > k) return sqrt(x2 + z2) * il2 - r2;',
    '  if (sign(y) * a2 * y2 < k) return sqrt(x2 + y2) * il2 - r1;',
    '  return (sqrt(x2 * a2 * il2) + y * rr) * il2 - r1;',
    '}',
    'float finger(vec3 hp, vec3 b0, vec3 d1, vec3 d2, vec3 d3, float r){',
    '  vec3 b1 = b0 + d1; vec3 b2 = b1 + d2; vec3 b3 = b2 + d3;',
    '  float f = sdRC(hp, b0, b1, r, r * 0.92);',
    '  f = sminC(f, sdRC(hp, b1, b2, r * 0.92, r * 0.86), 0.0045);',
    '  return sminC(f, sdRC(hp, b2, b3, r * 0.86, r * 0.8), 0.0045);',
    '}',
    'float hand(vec3 hp){',
    '  float palm = sdE(hp - vec3(-0.002, -0.029, 0.002), vec3(0.0102, 0.029, 0.0245));',
    '  if (length(hp - vec3(0.0, -0.065, 0.0)) > 0.1) return palm;',
    '  float fs = 1e9; float th = 1e9;',
    '  for (int i = 0; i < 5; i++) {',
    '    float fi = float(i);',
    '    float fz = 0.0172 - fi * 0.0116;',
    '    float fl = 0.0525 - abs(fi - 1.2) * 0.006;',
    '    bool tb = i == 4;',
    '    float f = finger(hp,',
    '      tb ? vec3(-0.004, -0.016, 0.02) : vec3(-0.003, -0.05, fz * 0.95),',
    '      tb ? vec3(-0.004, -0.017, 0.011) : vec3(-0.0015, -fl * 0.42, fz * 0.05),',
    '      tb ? vec3(-0.004, -0.015, 0.005) : vec3(-0.003, -fl * 0.32, 0.0045),',
    '      tb ? vec3(-0.003, -0.012, 0.002) : vec3(-0.0035, -fl * 0.24, 0.0065),',
    '      tb ? 0.0068 : 0.0056);',
    '    if (tb) th = f; else fs = min(fs, f);',
    '  }',
    '  return smin(palm, min(fs, th), 0.0065);',
    '}',
    'float head(vec3 p){',
    '  vec3 q = vec3(abs(p.x), p.y, p.z);',
    '  float h = sdE(p - vec3(0.0, 0.946, -0.009), vec3(0.0445, 0.0445, 0.0505));',
    '  h = sminC(h, sdE(p - vec3(0.0, 0.924, 0.011), vec3(0.0385, 0.046, 0.0385)), 0.042);',
    '  h = sminC(h, sdE(p - vec3(0.0, 0.942, 0.022), vec3(0.036, 0.02, 0.024)), 0.04);',
    '  h = sminC(h, sdE(q - vec3(0.023, 0.919, 0.026), vec3(0.012, 0.011, 0.013)), 0.024);',
    '  h = sminC(h, sdE(p - vec3(0.0, 0.899, 0.012), vec3(0.0315, 0.022, 0.0335)), 0.038);',
    '  h = sminC(h, sdE(p - vec3(0.0, 0.881, 0.031), vec3(0.0175, 0.0115, 0.0135)), 0.024);',
    '  h = sminC(h, sdE(q - vec3(0.028, 0.899, -0.003), vec3(0.0105, 0.015, 0.015)), 0.024);',
    '  float n = sdRC(p, vec3(0.0, 0.936, 0.046), vec3(0.0, 0.914, 0.0565), 0.0034, 0.0056);',
    '  n = smin(n, sdE(q - vec3(0.0062, 0.9135, 0.0505), vec3(0.0042, 0.0034, 0.0042)), 0.004);',
    '  h = sminC(h, n, 0.011);',
    '  h = smin(h, sdE(q - vec3(0.0435, 0.928, -0.009), vec3(0.0048, 0.0128, 0.0088)), 0.006);',
    '  return h;',
    '}',
    'float chestPlane(vec3 p){',
    '  float cx = uChest.x;',
    '  float fz = smoothstep(uChest.y * 0.3, uChest.y * 0.85, p.z);',
    '  if (fz <= 0.0) return 0.0;',
    '  float ax = sqrt(p.x * p.x + 0.00006);',
    '  float dx = ax - cx * 0.5; float dy = p.y - (uChest.z + 0.012);',
    '  float X = (dx * 0.94 + dy * 0.34) / (cx * 0.53); float Y = dy * 0.94 - dx * 0.34;',
    '  float ryb = mix(0.036, 0.05, 1.0 - uChest.w) * (1.0 + 0.45 * smoothstep(0.2, 1.0, X));',
    '  float sx = clamp(1.0 - X * X, 0.0, 1.0);',
    '  float s0 = Y < 0.0 ? clamp(1.0 - X * X - Y * Y / (ryb * ryb), 0.0, 1.0) : sx;',
    '  float v = mix(s0 * (2.0 - s0), s0 * s0 * (3.0 - 2.0 * s0), 0.78) * (Y < 0.0 ? 1.0 : exp(-Y * Y / 0.0042));',
    '  float pec = (0.0028 + 0.0024 * uChest.w) * v;',
    '  float cy = 0.797 + 0.009 * ax / cx;',
    '  float clav = 0.0005 * exp(-pow((p.y - cy) / 0.007, 2.0)) * smoothstep(0.1, 0.25, ax / cx) * (1.0 - smoothstep(0.7, 0.95, ax / cx));',
    '  float notch = 0.0009 * exp(-pow(ax / 0.011, 2.0) - pow((p.y - 0.801) / 0.01, 2.0));',
    '  return (pec + clav - notch) * fz;',
    '}',
    'vec2 map(vec3 p){',
    '  vec3 q = vec3(abs(p.x), p.y, p.z);',
    '  float armR = uLimb.x; float thighR = uLimb.y; float neckR = uLimb.z; float jx = uLimb.w;',
    '  float t = sdE(p - vec3(0.0, uChest.z - 0.005, -0.004), vec3(uChest.x, 0.09, uChest.y));',
    '  t = sminC(t, sdE(p - vec3(0.0, 0.762, -0.01), vec3(uChest.x * 0.93, 0.06, uChest.y * 0.9)), 0.06);',
    '  t = sminC(t, sdE(q - vec3(uChest.x * 0.52, 0.69, -uChest.y * 0.3), vec3(uChest.x * 0.34, 0.07, uChest.y * 0.55)), 0.04);',
    '  float tl = uChest.z - uHip.z;',
    '  t = sminC(t, sdE(p - vec3(0.0, uWaist.z, uWaist.w), vec3(uWaist.x, tl * 0.53, uWaist.y)), 0.085);',
    '  t = sminC(t, sdE(p - vec3(0.0, 0.622, uWaist.y * 0.42 + uWaist.w), vec3(uWaist.x * 0.68, tl * 0.43, uWaist.y * 0.6)), 0.065);',
    '  t = sminC(t, sdE(p - vec3(0.0, uHip.z, -0.006), vec3(uHip.x, 0.075, uHip.y)), 0.05);',
    '  t -= chestPlane(p);',
    '  t = sminC(t, sdE(q - vec3(uHip.x * 0.38, uHip.z - 0.015, -uHip.y * 0.3), vec3(uHip.x * 0.48, 0.065, uHip.y * 0.7)), 0.04);',
    '  t = sminC(t, sdE(p - vec3(0.0, 0.793, -0.012), vec3(jx * 0.92, 0.027, uChest.y * 0.62)), 0.06);',
    '  t = sminC(t, sdE(vec3(sqrt(p.x * p.x + 0.0001), p.y, p.z) - vec3(jx * 0.4, 0.809, -0.018), vec3(jx * 0.46, 0.026, uChest.y * 0.42)), 0.07);',
    '  t = sminC(t, sdE(q - vec3(jx - 0.003, 0.773, -0.005), vec3(armR * 1.05, armR * 1.3, armR * 1.08)), 0.05);',
    '  t = sminC(t, sdRC(p, vec3(0.0, 0.79, -0.018), vec3(0.0, 0.885, -0.006), neckR, neckR * 0.9), 0.033);',
    '  t = sminC(t, head(p), mix(0.024, 0.012, smoothstep(-0.02, 0.015, p.z)));',
    '  t = ssub(sdE(q - vec3(uChest.x * 1.0, 0.734, -0.01), vec3(0.015, 0.03, uChest.y * 0.38)), t, 0.022);',
    '  vec3 hj = vec3(uHip.x * 0.5, 0.5, 0.0);',
    '  vec3 kn = vec3(uArm.w, 0.285, 0.008);',
    '  vec3 an = vec3(0.058, 0.056, -0.01);',
    '  float kneeR = thighR * 0.52;',
    '  float ankR = max(0.0185, kneeR * 0.7);',
    '  float l = sdRC(q, hj, kn, thighR, kneeR * 0.95);',
    '  l = smin(l, sdE(q - mix(hj, kn, 0.4), vec3(thighR * 0.97, 0.14, thighR * 1.03)), 0.04);',
    '  l = smin(l, sdE(q - kn - vec3(0.0, 0.01, kneeR * 0.3), vec3(kneeR * 0.5, kneeR * 0.6, kneeR * 0.42)), 0.022);',
    '  l = smin(l, sdRC(q, kn, an, kneeR * 0.9, ankR), 0.03);',
    '  l = smin(l, sdE(q - vec3(mix(kn.x, an.x, 0.26) + 0.002, 0.218, -0.013), vec3(kneeR * 1.06, 0.085, kneeR * 1.15)), 0.045);',
    '  vec3 fq = q - vec3(an.x, 0.0, an.z);',
    '  float fs0 = 0.12; float fc = cos(fs0); float fsn = sin(fs0);',
    '  vec3 fl = vec3(fq.x * fc - fq.z * fsn, fq.y, fq.x * fsn + fq.z * fc);',
    '  float ft = sdE(fl - vec3(0.0, 0.02, -0.023), vec3(0.019, 0.02, 0.023));',
    '  ft = smin(ft, sdE(fl - vec3(0.002, 0.024, 0.024), vec3(0.0245, 0.024, 0.058)), 0.018);',
    '  ft = smin(ft, sdE(fl - vec3(0.004, 0.0145, 0.08), vec3(0.027, 0.0145, 0.032)), 0.026);',
    '  ft = -smin(-ft, fl.y - 0.0005, 0.004);',
    '  l = smin(l, ft, 0.022);',
    '  vec3 J = vec3(jx, 0.783, -0.006);',
    '  vec3 E = vec3(uArm.x, 0.625, -0.004);',
    '  vec3 W = vec3(uArm.y, 0.49, 0.006);',
    '  float re = armR * 0.74;',
    '  float a = sdRC(q, J, E, armR, re);',
    '  a = sminC(a, sdRC(q, E, W, re, uArm.z * 0.7), 0.03);',
    '  a = sminC(a, sdE(q - mix(J, E, 0.4) - vec3(0.003, 0.0, 0.003), vec3(armR * 0.92, 0.066, armR * 1.0)), 0.03);',
    '  a = sminC(a, sdE(q - mix(E, W, 0.3), vec3(re * 0.96, 0.045, re * 1.02)), 0.02);',
    '  a = smin(a, sdE(q - E - vec3(0.0, -0.004, -re * 0.72), vec3(re * 0.42, 0.011, re * 0.38)), 0.01);',
    '  vec2 fd = normalize((W - E).xy); float hs = fd.x; float hc = -fd.y;',
    '  vec3 wl = q - W; wl = vec3(hc * wl.x + hs * wl.y, -hs * wl.x + hc * wl.y, wl.z);',
    '  a = sminC(a, sdE(wl - vec3(0.0, 0.04, -0.001), vec3(uArm.z * 0.68, 0.042, uArm.z * 0.92)), 0.02);',
    '  a = sminC(a, sdE(wl - vec3(0.0, 0.002, 0.001), vec3(uArm.z * 0.58, 0.022, uArm.z * 0.88)), 0.014);',
    '  a = smin(a, hand(wl), 0.014);',
    '  float body = sminC(t, l, 0.05);',
    '  float ka = mix(0.0012, 0.006, smoothstep(0.69, 0.735, p.y));',
    '  ka = mix(ka, 0.014, smoothstep(0.76, 0.8, p.y));',
    '  float d = sminC(body, a, ka);',
    '  return vec2(d, a < body ? 1.0 : (l < t ? 2.0 : 0.0));',
    '}',
    'vec3 calcNormal(vec3 p, float e){ vec2 k = vec2(1.0, -1.0) * e; vec3 n = vec3(0.0); for (int i = 0; i < 4; i++) { vec3 o = i == 0 ? k.xyy : i == 1 ? k.yyx : i == 2 ? k.yxy : k.xxx; n += o * map(p + o).x; } return normalize(n); }',
    'vec2 calcAO(vec3 p, vec3 n){ float o = 0.0; float s = 1.0; float far = 0.0; for (int i = 0; i < 4; i++) { float hh = 0.005 + 0.004 * float(i) + 0.0035 * float(i * i); float d = map(p + n * hh).x; o += (hh - d) * s; s *= 0.72; far = d / hh; } return vec2(clamp(1.0 - 2.8 * o, 0.0, 1.0), clamp(far, 0.0, 1.0)); }',
    'float softShadow(vec3 ro, vec3 rd){ float res = 1.0; float t = 0.006; for (int i = 0; i < 24; i++) { float h = map(ro + rd * t).x; res = min(res, 9.0 * h / t); t += clamp(h, 0.006, 0.05); if (res < 0.02 || t > 0.5) break; } return clamp(res, 0.0, 1.0); }',
    'float armCore(vec3 q){ vec3 J = vec3(uLimb.w, 0.783, -0.006); vec3 E = vec3(uArm.x, 0.625, -0.004); vec3 W = vec3(uArm.y, 0.49, 0.006); return min(sdRC(q, J, E, uLimb.x, uLimb.x * 0.74), sdRC(q, E, W, uLimb.x * 0.74, uArm.z * 0.7)); }',
    'float torsoCore(vec3 p){ float t = sdE(p - vec3(0.0, uChest.z - 0.005, -0.004), vec3(uChest.x, 0.09, uChest.y)); t = sminC(t, sdE(p - vec3(0.0, uWaist.z, uWaist.w), vec3(uWaist.x, (uChest.z - uHip.z) * 0.53, uWaist.y)), 0.0675); return sminC(t, sdE(p - vec3(0.0, uHip.z, -0.006), vec3(uHip.x, 0.075, uHip.y)), 0.05); }',
    'float seg(vec2 p, vec2 a, vec2 b){ vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0); return length(pa - ba * h); }',
    'float abdomen(vec3 p, vec3 n){',
    '  if (n.z < 0.15) return 0.0;',
    '  float ax = abs(p.x); float w = uWaist.x;',
    '  float g = exp(-(pow(p.x / 0.0045, 2.0) + pow((p.y - 0.6) / 0.0055, 2.0))) * 0.45;',
    '  g += exp(-pow(ax / 0.012, 2.0)) * smoothstep(0.6, 0.64, p.y) * (1.0 - smoothstep(0.68, 0.71, p.y)) * 0.06;',
    '  return g * smoothstep(0.15, 0.5, n.z);',
    '}',
    'vec4 tape(vec3 p, vec4 g, vec4 c, vec3 vb, float pix, float occ){',
    '  if (g.w < 0.5) return vec4(0.0);',
    '  float focus = c.w;',
    '  float hw = focus > 0.5 ? 0.0105 : 0.0062;',
    '  float u = atan(p.z / g.z, p.x / g.y);',
    '  float dy = p.y - g.x;',
    '  float w = hw;',
    '  if (focus > 0.5) {',
    '    float uf = atan(vb.z / g.z, vb.x / g.y);',
    '    float du = mod(u - (uf + 0.45) + 3.14159, 6.28318) - 3.14159;',
    '    float ad = abs(du);',
    '    if (ad < 0.03) w = -1.0;',
    '    else if (ad < 0.36) w = hw * 2.1 * (ad - 0.03) / 0.33;',
    '  }',
    '  float aa = max(pix * 0.75, 0.0006);',
    '  float m = 1.0 - smoothstep(w - aa, w + aa, abs(dy));',
    '  float shadow = (1.0 - smoothstep(0.0, 0.0045, abs(dy) - max(w, 0.0))) * (1.0 - m) * 0.22;',
    '  float C = 3.14159 * (3.0 * (g.y + g.z) - sqrt((3.0 * g.y + g.z) * (g.y + 3.0 * g.z)));',
    '  float s = (u + 3.14159) / 6.28318 * C;',
    '  float tb = (dy + w) / max(2.0 * w, 0.0001);',
    '  float minor = (1.0 - smoothstep(0.08, 0.16, fract(s / uCm))) * step(tb, 0.36);',
    '  float major = (1.0 - smoothstep(0.03, 0.06, fract(s / (5.0 * uCm)))) * step(tb, 0.66);',
    '  float tick = max(minor, major) * step(hw * 0.9, w) * m;',
    '  float edge = (1.0 - smoothstep(0.0, aa * 2.0 + 0.0006, abs(abs(dy) - w + 0.0012))) * m * 0.25;',
    '  vec3 col = c.rgb + vec3(edge);',
    '  col = mix(col, vec3(0.94), tick * 0.85);',
    '  float echo = 0.0;',
    '  if (focus < 0.5) echo = (1.0 - smoothstep(0.0006, 0.0006 + aa * 2.0, abs(abs(dy) - hw - 0.011))) * 0.4;',
    '  float cover = max(m, echo);',
    '  return cover > 0.001 ? vec4(col, cover) : vec4(0.0, 0.0, 0.0, -shadow);',
    '}',
    'float gEdge;',
    'vec4 trace(vec2 fc){',
    '  vec2 uv = fc / uRes * 2.0 - 1.0;',
    '  float halfH = uView.z; float aspect = uRes.x / uRes.y;',
    '  vec3 pivot = vec3(0.0, uView.y, 0.0);',
    '  vec3 row = vec3(uView.x + uv.x * halfH * aspect, uView.y + uv.y * halfH, 1.5);',
    '  vec3 ro = uM * (row - pivot) + pivot;',
    '  vec3 rd = uM * vec3(0.0, 0.0, -1.0);',
    '  float pix = 2.0 * halfH / uRes.y;',
    '  float t = 0.0; float minD = 1e9; float tMin = 0.0; bool hit = false; float mat = 0.0; float matMin = 0.0;',
    '  gEdge = 0.0;',
    '  for (int i = 0; i < 140; i++) {',
    '    vec3 p = ro + rd * t;',
    '    vec2 r = map(p); float d = r.x; mat = r.y;',
    '    if (d < minD) { minD = d; tMin = t; matMin = r.y; }',
    '    if (d < 0.00035) { hit = true; break; }',
    '    t += max(d * 0.72, 0.0006);',
    '    if (t > 3.2) break;',
    '  }',
    '  if (!hit && minD > pix * 1.5) return vec4(0.0);',
    '  vec3 p = ro + rd * (hit ? t : tMin);',
    '  vec2 mm = vec2(0.0, hit ? mat : matMin);',
    '  vec3 n = calcNormal(p, max(0.0006, pix * 0.35));',
    '  vec3 v = -rd;',
    '  float ndv = clamp(dot(n, v), 0.0, 1.0);',
    '  if (!hit || ndv < 0.12) gEdge = 1.0;',
    '  vec3 kL = uM * normalize(vec3(-0.55, 0.6, 0.6));',
    '  vec3 fL = uM * normalize(vec3(0.75, 0.1, 0.55));',
    '  vec3 rL = uM * normalize(vec3(0.45, 0.45, -0.77));',
    '  vec3 up = uM * vec3(0.0, 1.0, 0.0);',
    '  vec3 alb = uSkin;',
    '  vec2 ao = calcAO(p, n);',
    '  float occ = ao.x * mix(0.55, 1.0, ao.y);',
    '  float shade = 1.0;',
    '  if (mm.y < 0.5) {',
    '    occ *= 1.0 - abdomen(p, n) * (0.2 + 0.35 * uChest.w);',
    '    for (int i = 0; i < 4; i++) {',
    '      vec4 tp = tape(p, uBandG[i], uBandC[i], v, pix, occ);',
    '      if (tp.a > 0.0) alb = mix(alb, tp.rgb, tp.a); else shade += tp.a;',
    '    }',
    '  }',
    '  if (mm.y > 0.5 && mm.y < 1.5 && uWrist.x > 0.5) {',
    '    vec3 q = vec3(abs(p.x), p.y, p.z);',
    '    vec3 E = vec3(uArm.x, 0.625, -0.004); vec3 W = vec3(uArm.y, 0.49, 0.006);',
    '    vec3 ax = normalize(W - E);',
    '    float sw = dot(q - W, ax) + 0.009;',
    '    float rad = length((q - W) - ax * dot(q - W, ax));',
    '    float aa = max(pix * 0.75, 0.0006);',
    '    float m = (1.0 - smoothstep(0.0042 - aa, 0.0042 + aa, abs(sw))) * step(rad, uArm.z * 1.8);',
    '    alb = mix(alb, uWrist.yzw, m);',
    '    shade -= (1.0 - smoothstep(0.0, 0.004, abs(sw) - 0.0042)) * (1.0 - m) * 0.2 * step(rad, uArm.z * 1.8);',
    '  }',
    '  vec3 qa = vec3(abs(p.x), p.y, p.z);',
    '  float other = (mm.y > 0.5 && mm.y < 1.5) ? torsoCore(p) : armCore(qa);',
    '  float contact = (1.0 - smoothstep(0.0, 0.045, other)) * (1.0 - smoothstep(0.72, 0.77, p.y));',
    '  contact = contact * contact * 0.6;',
    '  float ksh = 1.0; if (uQ > 0.5 && dot(n, kL) > 0.0) ksh = mix(0.42, 1.0, softShadow(p + n * 0.002, kL));',
    '  float ndl = dot(n, kL);',
    '  float key = clamp((ndl + 0.1) / 1.1, 0.0, 1.0) * (1.0 - contact) * ksh;',
    '  float fill = clamp((dot(n, fL) + 0.2) / 1.2, 0.0, 1.0);',
    '  float fres = pow(1.0 - ndv, 5.0);',
    '  float rim = clamp(dot(n, rL), 0.0, 1.0) * pow(1.0 - ndv, 1.6);',
    '  float sky = dot(n, up) * 0.5 + 0.5;',
    '  vec3 hv = normalize(kL + v); float ndh = clamp(dot(n, hv), 0.0, 1.0);',
    '  float F = 0.04 + 0.96 * pow(1.0 - clamp(dot(v, hv), 0.0, 1.0), 5.0);',
    '  float spec = (pow(ndh, 56.0) * 2.8 + pow(ndh, 10.0) * 0.7) * F * clamp(ndl, 0.0, 1.0) * ksh * (1.0 - contact);',
    '  float sss = (smoothstep(-0.32, 0.05, ndl) - smoothstep(0.05, 0.45, ndl)) * ksh;',
    '  vec3 col = alb * (vec3(0.185) * mix(0.38, 1.0, sky) * occ * (1.0 - contact * 0.7)',
    '                 + vec3(1.0, 0.975, 0.94) * 1.0 * key * key * mix(0.55, 1.0, occ)',
    '                 + vec3(0.86, 0.9, 1.0) * 0.17 * fill * occ * (1.0 - contact * 0.5)',
    '                 + vec3(0.05, 0.025, 0.016) * sss * occ)',
    '            + vec3(1.0) * (0.36 * rim + 0.04 * fres * sky) * occ + vec3(spec) * occ;',
    '  col *= 1.0 - 0.17 * pow(1.0 - ndv, 2.0) * (1.0 - clamp(dot(n, rL), 0.0, 1.0));',
    '  col *= clamp(shade, 0.6, 1.0);',
    '  col = col / (1.0 + col * 0.12) * 1.06;',
    '  col = pow(clamp(col, 0.0, 1.0), vec3(0.4545));',
    '  float alpha = hit ? 1.0 : 1.0 - smoothstep(0.0, pix * 1.5, minD);',
    '  return vec4(col * alpha, alpha);',
    '}',
    'void main(){',
    '  vec2 fc = gl_FragCoord.xy;',
    '  vec4 acc = vec4(0.0); float count = 0.0; float edge = 0.0;',
    '  for (int s = 0; s < 5; s++) {',
    '    if (s > 0 && (edge < 0.5 || float(s) > uAA)) break;',
    '    vec2 o = s == 1 ? vec2(0.375, 0.125) : s == 2 ? vec2(-0.375, -0.125) : s == 3 ? vec2(0.125, -0.375) : s == 4 ? vec2(-0.125, 0.375) : vec2(0.0);',
    '    acc += trace(fc + o); count += 1.0;',
    '    if (s == 0) edge = gEdge;',
    '  }',
    '  vec4 outc = acc / count;',
    '  vec2 uv = fc / uRes * 2.0 - 1.0;',
    '  float halfH = uView.z; float aspect = uRes.x / uRes.y;',
    '  vec3 pivot = vec3(0.0, uView.y, 0.0);',
    '  vec3 ro = uM * (vec3(uView.x + uv.x * halfH * aspect, uView.y + uv.y * halfH, 1.5) - pivot) + pivot;',
    '  vec3 rd = uM * vec3(0.0, 0.0, -1.0);',
    '  if (uView.w > 0.5 && outc.a < 1.0 && rd.y < -0.001) {',
    '    float tf = -ro.y / rd.y;',
    '    vec3 fp = ro + rd * tf;',
    '    float gd = map(fp + vec3(0.0, 0.01, 0.0)).x; float g1 = 1.0 - smoothstep(0.0, 0.035, gd); float sh = (g1 * g1 * 0.2 + (1.0 - smoothstep(0.0, 0.12, gd)) * 0.09) * (1.0 - smoothstep(0.15, 0.4, abs(fp.z)));',
    '    outc = outc + vec4(0.0, 0.0, 0.0, sh) * (1.0 - outc.a);',
    '  }',
    '  gl_FragColor = outc;',
    '}'
  ].join('\n');

  var VERT = 'attribute vec2 aPos; void main(){ gl_Position = vec4(aPos, 0.0, 1.0); }';

  var YAW = 0.5;      /* three-quarter turn */
  /* Edge supersampling: 2 extra rays per outline pixel on phones, 4 elsewhere */
  var AA_LEVEL = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || window.innerWidth < 750 ? 2 : 4;
  var PITCH = 0.09;   /* seen from slightly above */
  var BAND_Y = { chest: 0.722, waist: 0.615, hip: 0.525, neck: 0.855 };
  var BAND_AREAS = ['chest', 'waist', 'hip', 'neck'];
  var BAND_COLORS = { ok: '#2E6A4D', warn: '#A2701C', bad: '#A23A2C', focus: '#262626' };

  /* Camera framings (body-height units). Zoomed ones follow the area being measured. */
  var VIEWS = {
    full: { top: 1.03, bottom: 0.27, cx: 0 },
    result: { top: 1.03, bottom: 0.33, cx: 0 },
    fullBottom: { top: 1.03, bottom: -0.035, cx: 0 },
    resultBottom: { top: 0.74, bottom: -0.02, cx: 0 },
    chest: { top: 1.0, bottom: 0.5, cx: 0 },
    waist: { top: 0.88, bottom: 0.42, cx: 0 },
    hip: { top: 0.72, bottom: 0.3, cx: 0 },
    neck: { top: 1.0, bottom: 0.74, cx: 0 },
    arm: { top: 0.94, bottom: 0.4, cx: 0.035 },
    inseam: { top: 1.03, bottom: -0.04, cx: 0 }
  };

  function hexToLinear(hex) {
    var n = parseInt(String(hex).replace('#', ''), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(function (c) { return Math.pow(c / 255, 2.2); });
  }

  /* body = M * world (rows), returned column-major for WebGL */
  function rotation() {
    var cy = Math.cos(YAW), sy = Math.sin(YAW), cp = Math.cos(PITCH), sp = Math.sin(PITCH);
    var m = [[cy, sy * sp, sy * cp], [0, cp, -sp], [-sy, cy * sp, cy * cp]];
    return { rows: m, cols: [m[0][0], m[1][0], m[2][0], m[0][1], m[1][1], m[2][1], m[0][2], m[1][2], m[2][2]] };
  }
  var ROT = rotation();

  /* Body point -> world point for a view pivoting at height cy. world = M^T (body - pivot) + pivot */
  function toWorld(b, cy) {
    var m = ROT.rows; var by = b[1] - cy;
    return [m[0][0] * b[0] + m[1][0] * by + m[2][0] * b[2],
            m[0][1] * b[0] + m[1][1] * by + m[2][1] * b[2] + cy,
            m[0][2] * b[0] + m[1][2] * by + m[2][2] * b[2]];
  }

  /* Visual caps for "My measurements": around where each slider starts the figure follows the
     numbers 1:1, then eases off towards the ends and never moves more than 3/10 of the slider
     range either way, i.e. it stays within a 3/5-wide window (soft saturation, so dragging
     always moves it a little and nothing snaps). On top of that,
     anatomical caps keep the neck and wrist in proportion to the head and hand. The size maths
     always uses the real, uncapped numbers. */
  var SHAPE_CAPS = { neck: [33, 43], wrist: [14.5, 19.5] };
  function softCap(area, v, mid) {
    var lim = MEAS_LIMITS[area]; if (!lim) return v;
    var half = (lim[1] - lim[0]) * 0.3;   /* +-30% of the range = the middle 3/5 */
    var out = mid + half * Math.tanh((v - mid) / half);
    var cap = SHAPE_CAPS[area];
    return cap ? clamp(out, cap[0], cap[1]) : out;
  }

  /* The figure exaggerates the shopper's changes a little so they are easy to see. */
  function visualBody(profile) {
    var adjust = profile.adjust || {};
    var scaled = {};
    Object.keys(adjust).forEach(function (k) { scaled[k] = (adjust[k] || 0) * 1.5; });
    var body = estimate(Object.assign({}, profile, { adjust: scaled }));
    /* centred on where each slider starts (the shopper's own estimate), so untouched sliders
       draw exactly as before and only the far ends are eased in */
    var base = estimate(Object.assign({}, profile, { adjust: {} }));
    Object.keys(body).forEach(function (k) { body[k] = softCap(k, body[k], base[k]); });
    return body;
  }

  function figureParams(profile, body) {
    var H = profile.height;
    var k = clamp(bmiOf(profile), 15, 45) - 22;
    var kl = Math.min(k, 7);                           /* limbs stop filling out past a point */
    var full = 1.08;                                   /* a touch fuller, like a display mannequin */
    function half(circ, ratio) { return circ / (Math.PI * (1 + ratio)) / H * full; }
    /* Respectful cap: the figure never shows a belly rounder than a moderate one,
       whatever the numbers say (the size maths still uses the real numbers). */
    /* Kind caps: whatever the numbers, the figure stays a regular, solid build. The belly never
       passes the chest line and never reads as heavy; the size maths still uses the real numbers. */
    var chestV = Math.min(body.chest, 118);
    var waistV = Math.min(body.waist, chestV - 2, 100);
    var hipV = Math.min(body.hip, Math.max(waistV, chestV) + 4, 112);
    /* chest depth/width 0.8: same circumference as before, a little narrower and deeper, so the
       arms can hang clear of the ribcage without widening the shoulders */
    var cr = 0.8, wr = clamp(0.76 + k * 0.01, 0.7, 0.86), hr = 0.78;
    var chestA = half(chestV, cr), waistA = half(waistV, wr);
    /* hips without the display fullness: a man's hips sit well inside his shoulders, and with the
       seat and thighs blended on, the extra 8% made the pelvis read wide even on default numbers */
    var hipA = half(hipV, hr) / full * 0.95;
    var armR = (3.6 + kl * 0.12) / H * full;           /* slim, mannequin arms */
    var joint = chestA * 1.0;                          /* shoulder joint, just outside the chest line */
    /* wrist and neck follow their (capped) measurements, gently, around the build-based value */
    var wristR = (2.9 + kl * 0.05) * clamp((body.wrist || 17) / 17, 0.88, 1.12) / H * full;
    var wristX = Math.max(hipA, waistA) + wristR + 0.05;
    /* the neck is drawn true to its circumference (no extra fullness), so it reads slimmer than
       the jaw from the front instead of running straight up into the head */
    var neckR = clamp(body.neck || 39, 33, 43) / (2 * Math.PI) / H;
    return {
      chestA: chestA, chestB: chestA * cr,
      waistA: waistA, waistB: waistA * wr, belly: clamp(k * 0.0012, -0.004, 0.008),
      hipA: hipA, hipB: hipA * hr,
      armR: armR, thighR: hipA * 0.47, neckR: neckR, joint: joint,
      /* Arms hang slightly away from the body (about 11 deg), straight from shoulder to wrist:
         the elbow sits on the shoulder-wrist line (0.539 = its share of the drop from 0.783 to
         0.49), so the forearm never turns back in at the elbow. The hand clears the thigh. */
      elbowX: joint + (wristX - joint) * 0.539,
      wristX: wristX,
      wristR: wristR,
      kneeX: hipA * 0.42 + 0.02,
      pecs: clamp(1 - (k - 3) / 8, 0, 1),
      cm: 1 / H
    };
  }

  /* ---------- Shared renderer ----------
     Every figure (shape, result, measure, phone versions) draws with ONE WebGL context and
     ONE compiled shader, then copies the frame into its own 2D canvas. Compiling the shader is
     the slow part of opening the fitting room, so it happens once per page instead of once per
     figure, and it can start before the shopper even opens the fitting room (see prewarm). */
  var Shared = { state: 'idle', canvas: null, gl: null, u: null, parallel: null, error: '', waiters: [] };

  function sharedContext() {
    if (Shared.gl) return Shared.gl;
    var canvas = document.createElement('canvas');
    canvas.width = 1; canvas.height = 1;
    var gl = null;
    try { gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false, preserveDrawingBuffer: false }); } catch (e) { gl = null; }
    if (!gl) return null;
    canvas.addEventListener('webglcontextlost', function (event) {
      event.preventDefault();
      Shared.state = 'idle'; Shared.u = null;
    });
    canvas.addEventListener('webglcontextrestored', function () { sharedCompile(); });
    Shared.canvas = canvas; Shared.gl = gl;
    Shared.parallel = gl.getExtension('KHR_parallel_shader_compile');
    return gl;
  }

  function sharedDone(error) {
    Shared.state = error ? 'failed' : 'ready';
    Shared.error = error || '';
    var list = Shared.waiters; Shared.waiters = [];
    list.forEach(function (cb) { cb(error); });
  }

  /* Starts the compile once; later calls do nothing. */
  function sharedCompile() {
    if (Shared.state === 'compiling' || Shared.state === 'ready' || Shared.state === 'failed') return;
    var gl = sharedContext();
    if (!gl) { sharedDone('WebGL is not available'); return; }
    Shared.state = 'compiling';
    try {
      var vs = gl.createShader(gl.VERTEX_SHADER); gl.shaderSource(vs, VERT); gl.compileShader(vs);
      var fs = gl.createShader(gl.FRAGMENT_SHADER); gl.shaderSource(fs, FRAG); gl.compileShader(fs);
      var prog = gl.createProgram();
      gl.attachShader(prog, vs); gl.attachShader(prog, fs);
      gl.linkProgram(prog);
      var finish = function () {
        try {
          if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
            throw new Error(gl.getShaderInfoLog(fs) || gl.getShaderInfoLog(vs) || gl.getProgramInfoLog(prog) || 'Shader failed to compile');
          }
          gl.useProgram(prog);
          var buf = gl.createBuffer();
          gl.bindBuffer(gl.ARRAY_BUFFER, buf);
          gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
          var loc = gl.getAttribLocation(prog, 'aPos');
          gl.enableVertexAttribArray(loc);
          gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
          var u = {};
          ['uRes', 'uView', 'uM', 'uSkin', 'uCm', 'uAA', 'uChest', 'uWaist', 'uHip', 'uLimb', 'uArm', 'uBandG', 'uBandC', 'uWrist', 'uQ'].forEach(function (name) {
            u[name] = gl.getUniformLocation(prog, name);
          });
          Shared.u = u;
          /* One tiny draw makes drivers that compile lazily finish now, not on the first real frame */
          Shared.canvas.width = 1; Shared.canvas.height = 1;
          gl.viewport(0, 0, 1, 1);
          gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
          sharedDone(null);
        } catch (err) {
          sharedDone(err.message);
        }
      };
      /* Where the browser compiles in the background, wait for it without blocking the page */
      if (Shared.parallel) {
        var started = Date.now();
        (function poll() {
          if (gl.getProgramParameter(prog, Shared.parallel.COMPLETION_STATUS_KHR) || Date.now() - started > 20000) finish();
          else window.setTimeout(poll, 50);
        })();
      } else {
        finish();
      }
    } catch (err) {
      sharedDone(err.message);
    }
  }

  /* Warm the renderer before the first click: once the fitting room button is near the screen
     and the page is idle. Only where the compile runs off the main thread, so scrolling and
     typing never stutter; elsewhere it starts when the fitting room opens (warmOnOpen). */
  var prewarmed = false;
  function prewarm(trigger) {
    if (prewarmed || !trigger) return;
    prewarmed = true;
    var idle = window.requestIdleCallback || function (cb) { return window.setTimeout(cb, 1200); };
    function go() {
      idle(function () {
        var gl = sharedContext();
        if (gl && Shared.parallel) sharedCompile();
      }, { timeout: 4000 });
    }
    function afterLoad() {
      if (!window.IntersectionObserver) { go(); return; }
      var io = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) { io.disconnect(); go(); }
      }, { rootMargin: '600px 0px' });
      io.observe(trigger);
    }
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, { once: true });
  }

  function warmOnOpen() {
    if (Shared.state !== 'idle') return;
    /* Let the dialog paint first; the shopper is still filling in the form */
    window.setTimeout(sharedCompile, 350);
  }

  function Figure(holder, options) {
    this.holder = holder;
    this.canvas = qs('canvas', holder);
    this.leaders = qs('[data-fr-leaders]', holder);
    this.options = options || {};
    this.view = Object.assign({}, VIEWS[this.options.view || 'full']);
    this.params = null;
    this.bands = [];
    this.overlay = null;
    this.wrist = false;
    this.skin = hexToLinear('#EEEEEC');
    this.frame = null;
    this.viewFrame = null;
    this.ctx = null;
    this.ready = false;
    var self = this;
    /* Loading / error message shown in place of the figure until its first frame is drawn */
    var strings = this.options.strings || {};
    this.strings = strings;
    var status = document.createElement('div');
    status.className = 'fr__figure-status';
    status.setAttribute('role', 'status');
    var spinner = document.createElement('span');
    spinner.className = 'fr__figure-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    var text = document.createElement('p');
    text.className = 'fr__figure-status-text';
    status.appendChild(spinner);
    status.appendChild(text);
    holder.appendChild(status);
    this.statusText = text;
    if (window.ResizeObserver) {
      new ResizeObserver(function () { if (self.params) self.render(); }).observe(holder);
    }
  }

  Figure.prototype.setStatus = function (state) {
    var h = this.holder;
    h.classList.toggle('is-loading', state === 'loading');
    h.classList.toggle('is-ready', state === 'ready');
    h.classList.toggle('is-failed', state === 'failed');
    h.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    this.statusText.textContent = state === 'loading' ? (this.strings.loading || '') : state === 'failed' ? (this.strings.error || '') : '';
  };

  /* Returns true once the shared renderer is ready. Until then the figure shows its loading
     state, asks for the compile (a no-op if it is already running) and redraws when it ends. */
  Figure.prototype.setup = function () {
    if (this.ctx && Shared.state === 'ready') return true;
    if (this.failed) return false;
    if (Shared.state === 'ready') {
      this.ctx = this.canvas.getContext('2d');
      if (!this.ctx) { this.fail('Canvas is not available'); return false; }
      return true;
    }
    if (Shared.state === 'failed') { this.fail(Shared.error); return false; }
    if (!this.pending) {
      var self = this;
      this.pending = true;
      this.setStatus('loading');
      Shared.waiters.push(function (error) {
        self.pending = false;
        if (error) self.fail(error); else self.render();
      });
      /* Paint the loading state before a compile that may block (no background compile) */
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(sharedCompile);
      });
    }
    return false;
  };

  Figure.prototype.fail = function (reason) {
    if (window.console && reason) console.warn('Fitting room figure:', reason);
    this.failed = true;
    this.pending = false;
    this.holder.classList.add('fr__figure--static');
    this.setStatus('failed');
    if (this.options.onSettled) this.options.onSettled();
  };

  Figure.prototype.setTone = function (hex) {
    this.skin = hexToLinear(hex);
    if (this.params) this.render();
  };

  /* bands: [{ area, level }]; overlay: 'arm' | 'inseam' | null; wrist: draw the wrist tape */
  Figure.prototype.setBands = function (bands, overlay, wrist) {
    this.bands = bands || [];
    this.overlay = overlay || null;
    this.wrist = !!wrist;
  };

  /* Move the camera to a framing; zooms smoothly when animate is true. */
  Figure.prototype.setView = function (target, animate) {
    var self = this;
    if (self.viewFrame) { window.cancelAnimationFrame(self.viewFrame); self.viewFrame = null; }
    if (!animate || reducedMotion || !self.params) { self.view = Object.assign({}, target); return; }
    var from = Object.assign({}, self.view); var start = null; var duration = 480;
    function step(time) {
      if (start === null) start = time;
      var t = Math.min(1, (time - start) / duration);
      var e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      self.view = { top: lerp(from.top, target.top, e), bottom: lerp(from.bottom, target.bottom, e), cx: lerp(from.cx, target.cx, e) };
      self.render(t < 1);
      self.viewFrame = t < 1 ? window.requestAnimationFrame(step) : null;
    }
    self.viewFrame = window.requestAnimationFrame(step);
  };

  /* Framing centred on the near wrist (the one on the right of the screen). */
  Figure.prototype.wristView = function () {
    var P = this.params;
    var cy = 0.5;
    var w = toWorld([P.wristX, 0.47, 0.006], cy);
    return { top: w[1] + 0.085, bottom: w[1] - 0.095, cx: w[0] - 0.01 };
  };

  Figure.prototype.bandY = function (area) {
    return area === 'waist' && this.options.waistY ? this.options.waistY : BAND_Y[area];
  };

  /* Screen position (0-1 from the top) of a band's front edge. */
  Figure.prototype.bandTop = function (area) {
    var P = this.params; var v = this.view;
    var depth = area === 'chest' ? P.chestB : area === 'waist' ? P.waistB : area === 'hip' ? P.hipB : P.neckR;
    if (area === 'waist' && this.options.waistY) depth = lerp(P.waistB, P.hipB, 0.4);
    var cy = (v.top + v.bottom) / 2;
    var w = toWorld([0, this.bandY(area), depth], cy);
    return (v.top - w[1]) / (v.top - v.bottom);
  };

  /* Screen position (0-1 from the top) of a body height on the centre line (e.g. mid inseam). */
  Figure.prototype.yTop = function (y) {
    var v = this.view; var cy = (v.top + v.bottom) / 2;
    return (v.top - toWorld([0, y, 0], cy)[1]) / (v.top - v.bottom);
  };

  /* Body point -> canvas CSS pixels for the current view. */
  Figure.prototype.project = function (b, w, h) {
    var v = this.view; var cy = (v.top + v.bottom) / 2; var halfH = (v.top - v.bottom) / 2;
    var p = toWorld(b, cy);
    return [((p[0] - v.cx) / (halfH * w / h) + 1) / 2 * w, (1 - (p[1] - cy) / halfH) / 2 * h];
  };

  /* draft = true while the figure is moving (zoom, morph, slider drag): the frame is drawn at 60%
     resolution and stretched by CSS, which cuts the work by ~64% exactly when frames come back
     to back. A full-resolution frame follows as soon as the movement stops. */
  Figure.prototype.render = function (draft) {
    var P = this.params;
    if (!P) return;
    var w = this.holder.clientWidth; var h = this.holder.clientHeight;
    if (!w || !h) return;
    if (!this.setup()) return;
    var gl = Shared.gl; var u = Shared.u;
    /* Phones render at up to 1.5x (edge supersampling keeps the outline crisp); other screens up to 2x */
    var dpr = Math.min(window.devicePixelRatio || 1, AA_LEVEL === 2 ? 1.5 : 2) * (draft ? 0.6 : 1);
    var cw = Math.round(w * dpr); var ch = Math.round(h * dpr);
    if (this.canvas.width !== cw || this.canvas.height !== ch) { this.canvas.width = cw; this.canvas.height = ch; }
    if (Shared.canvas.width !== cw || Shared.canvas.height !== ch) { Shared.canvas.width = cw; Shared.canvas.height = ch; }
    gl.viewport(0, 0, cw, ch);
    var v = this.view;
    gl.uniform2f(u.uRes, cw, ch);
    gl.uniform4f(u.uView, v.cx || 0, (v.top + v.bottom) / 2, (v.top - v.bottom) / 2, this.options.ground ? 1 : 0);
    gl.uniformMatrix3fv(u.uM, false, new Float32Array(ROT.cols));
    gl.uniform3fv(u.uSkin, this.skin);
    gl.uniform1f(u.uCm, P.cm);
    gl.uniform1f(u.uAA, AA_LEVEL);
    gl.uniform1f(u.uQ, draft ? 0 : 1);   /* soft shadows only on still frames */
    gl.uniform4f(u.uChest, P.chestA, P.chestB, BAND_Y.chest, P.pecs);
    gl.uniform4f(u.uWaist, P.waistA, P.waistB, BAND_Y.waist, P.belly);
    gl.uniform4f(u.uHip, P.hipA, P.hipB, BAND_Y.hip, 0);
    gl.uniform4f(u.uLimb, P.armR, P.thighR, P.neckR, P.joint);
    gl.uniform4f(u.uArm, P.elbowX, P.wristX, P.wristR, P.kneeX);
    var size = { chest: [P.chestA, P.chestB], waist: [P.waistA, P.waistB], hip: [P.hipA, P.hipB], neck: [P.neckR, P.neckR * 0.95] };
    if (this.options.waistY) size.waist = [lerp(P.waistA, P.hipA, 0.4), lerp(P.waistB, P.hipB, 0.4)];
    var g = new Float32Array(16); var c = new Float32Array(16);
    var self = this;
    BAND_AREAS.forEach(function (area, i) {
      var band = self.bands.filter(function (b) { return b.area === area; })[0];
      g[i * 4] = self.bandY(area); g[i * 4 + 1] = size[area][0] + 0.0004; g[i * 4 + 2] = size[area][1] + 0.0004; g[i * 4 + 3] = band ? 1 : 0;
      var col = hexToLinear(BAND_COLORS[band ? band.level : 'focus']);
      c[i * 4] = col[0]; c[i * 4 + 1] = col[1]; c[i * 4 + 2] = col[2]; c[i * 4 + 3] = band && band.level === 'focus' ? 1 : 0;
    });
    gl.uniform4fv(u.uBandG, g);
    gl.uniform4fv(u.uBandC, c);
    var wc = hexToLinear(BAND_COLORS.focus);
    gl.uniform4f(u.uWrist, this.wrist ? 1 : 0, wc[0], wc[1], wc[2]);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    /* Copy right away, while the shared buffer still holds this figure's frame */
    this.ctx.clearRect(0, 0, cw, ch);
    this.ctx.drawImage(Shared.canvas, 0, 0);
    if (!this.frame) this.drawLeaders(cw, ch, w, h);
    if (!this.ready) {
      /* First frame is drawn: cross-fade from the loading state on the next frame */
      var me = this;
      this.ready = true;
      window.requestAnimationFrame(function () {
        me.setStatus('ready');
        if (me.options.onSettled) me.options.onSettled();
      });
    }
  };

  /* Overlays in CSS pixels: dotted leaders to the fit labels, and the arm / inseam arrows. */
  Figure.prototype.drawLeaders = function (cw, ch, w, h) {
    var layer = this.leaders;
    if (!layer) return;
    layer.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
    while (layer.firstChild) layer.removeChild(layer.firstChild);
    var gl = Shared.gl; var self = this; var P = this.params;
    var px = new Uint8Array(cw * 4);
    this.bands.forEach(function (b) {
      if (b.level === 'focus') return;
      var top = self.bandTop(b.area);
      var row = Math.round((1 - top) * ch);
      gl.readPixels(0, clamp(row, 0, ch - 1), cw, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
      var edge = 0;
      for (var x = cw - 1; x >= 0; x--) { if (px[x * 4 + 3] > 120) { edge = x; break; } }
      var y = top * h;
      svg('line', { x1: (edge / cw * w + 6).toFixed(1), y1: y.toFixed(1), x2: String(w), y2: y.toFixed(1), class: 'fr-leader fr-leader--' + b.level }, layer);
    });
    function arrowHead(tip, from, size) {
      var dx = tip[0] - from[0]; var dy = tip[1] - from[1]; var len = Math.sqrt(dx * dx + dy * dy) || 1;
      dx /= len; dy /= len;
      var bx = tip[0] - dx * size; var by = tip[1] - dy * size;
      return 'M' + tip[0].toFixed(1) + ' ' + tip[1].toFixed(1) + ' L' + (bx - dy * size * 0.62).toFixed(1) + ' ' + (by + dx * size * 0.62).toFixed(1) +
        ' L' + (bx + dy * size * 0.62).toFixed(1) + ' ' + (by - dx * size * 0.62).toFixed(1) + ' Z';
    }
    if (this.overlay === 'arm') {
      /* Shoulder tip, round the elbow, down to the wrist bone, just outside the near arm */
      var o = P.armR * 1.25;
      var pts = [[P.joint + 0.004, 0.808, -0.01], [P.joint + P.armR * 1.05 + o * 0.6, 0.75, -0.02], [P.elbowX + o, 0.625, -0.014], [P.wristX + P.wristR + o * 0.7, 0.53, -0.002], [P.wristX + P.wristR * 0.6, 0.497, 0.006]]
        .map(function (b) { return self.project(b, w, h); });
      var d = 'M' + pts[0][0].toFixed(1) + ' ' + pts[0][1].toFixed(1);
      for (var i = 1; i < pts.length; i++) {
        var p0 = pts[Math.max(i - 2, 0)], p1 = pts[i - 1], p2 = pts[i], p3 = pts[Math.min(i + 1, pts.length - 1)];
        d += ' C' + (p1[0] + (p2[0] - p0[0]) / 6).toFixed(1) + ' ' + (p1[1] + (p2[1] - p0[1]) / 6).toFixed(1) + ' ' +
          (p2[0] - (p3[0] - p1[0]) / 6).toFixed(1) + ' ' + (p2[1] - (p3[1] - p1[1]) / 6).toFixed(1) + ' ' + p2[0].toFixed(1) + ' ' + p2[1].toFixed(1);
      }
      var stroke = Math.max(5, h * 0.016);
      svg('path', { d: d, class: 'fr-guide' , 'stroke-width': stroke.toFixed(1) }, layer);
      svg('path', { d: arrowHead(pts[pts.length - 1], pts[pts.length - 2], stroke * 2.6), class: 'fr-guide__head' }, layer);
    }
    if (this.overlay === 'inseam') {
      var top = self.project([0, 0.474, 0.0], w, h);
      var floor = self.project([0, 0.0, 0.0], w, h);
      var span = (P.hipA * 1.3) / ((this.view.top - this.view.bottom) / 2) * h / 2;
      svg('line', { x1: (top[0] - span).toFixed(1), y1: top[1].toFixed(1), x2: (top[0] + span).toFixed(1), y2: top[1].toFixed(1), class: 'fr-guide__rule' }, layer);
      svg('line', { x1: (floor[0] - span).toFixed(1), y1: floor[1].toFixed(1), x2: (floor[0] + span).toFixed(1), y2: floor[1].toFixed(1), class: 'fr-guide__rule' }, layer);
      var sw = Math.max(4, h * 0.012);
      var tip = [floor[0], floor[1] - 2];
      svg('line', { x1: top[0].toFixed(1), y1: (top[1] + 2).toFixed(1), x2: tip[0].toFixed(1), y2: (tip[1] - sw * 2.4).toFixed(1), class: 'fr-guide', 'stroke-width': sw.toFixed(1) }, layer);
      svg('path', { d: arrowHead(tip, top, sw * 2.6), class: 'fr-guide__head' }, layer);
    }
  };

  Figure.prototype.update = function (P, animate, draft) {
    var self = this;
    if (self.frame) { window.cancelAnimationFrame(self.frame); self.frame = null; }
    if (!self.params || !animate || reducedMotion) {
      self.params = P; self.render(draft); return;
    }
    var from = self.params; var start = null; var duration = 300;
    function step(time) {
      if (start === null) start = time;
      var t = Math.min(1, (time - start) / duration);
      var e = 1 - Math.pow(1 - t, 3);
      var now = {};
      Object.keys(P).forEach(function (key) { now[key] = lerp(from[key], P[key], e); });
      self.params = now;
      if (t < 1) { self.frame = window.requestAnimationFrame(step); self.render(true); }
      else { self.frame = null; self.params = P; self.render(); }
    }
    self.frame = window.requestAnimationFrame(step);
  };

  /* ---------- Fit icons for the labels ---------- */

  function fitIcon(key, level) {
    var icon = svg('svg', { viewBox: '0 0 24 24', class: 'fr-label__icon fr-label__icon--' + level, 'aria-hidden': 'true', focusable: 'false' });
    svg('circle', { cx: '12', cy: '12', r: '11' }, icon);
    var d;
    if (level === 'bad') d = 'M8.5 8.5l7 7M15.5 8.5l-7 7';
    else if (key === 'ideal') d = 'M7.5 12.3l3 3 6-6.6';
    else if (key.indexOf('tight') !== -1) d = 'M4.5 12h5M7.5 9.5 10 12l-2.5 2.5M19.5 12h-5M16.5 9.5 14 12l2.5 2.5';
    else d = 'M10 12H4.5M7 9.5 4.5 12 7 14.5M14 12h5.5M17 9.5l2.5 2.5-2.5 2.5';
    svg('path', { d: d }, icon);
    return icon;
  }

  /* ---------- Controller ---------- */

  function FittingRoom(root) {
    this.root = root;
    var configEl = qs('[data-fr-config]', root);
    try { this.config = JSON.parse(configEl.textContent); } catch (e) { return; }
    this.strings = this.config.strings || {};
    this.table = parseTable(this.config.table);
    if (!this.table.sizes.length || !this.table.areas.length) { root.hidden = true; return; }

    this.blockId = root.getAttribute('data-block-id');
    this.product = root.closest('[data-product]') || document;
    this.trigger = qs('[data-fr-open]', root);
    this.triggerLabel = qs('[data-fr-trigger-label]', root);
    this.triggerDefault = this.triggerLabel.textContent;

    /* The block lives inside the product form. Move the dialog to <body> so its fields
       never submit the form (pressing Enter would otherwise add to cart). */
    var dialog = qs('[data-fr-dialog]', root);
    qsa('body > dialog#' + CSS.escape(dialog.id)).forEach(function (old) { if (old !== dialog) old.remove(); });
    document.body.appendChild(dialog);
    this.dialog = dialog;
    this.frame = qs('[data-fr-frame]', dialog);

    var saved = storageGet();
    this.units = (saved && saved.units) || this.config.units || 'metric';
    this.profile = saved && saved.height ? saved : null;
    this.adjust = { chest: 0, waist: 0, hip: 0 };
    this.tone = 0;
    this.type = this.config.type === 'bottom' ? 'bottom' : 'top';
    this.focusArea = this.type === 'bottom' ? 'waist' : 'chest';
    if (this.profile) {
      this.adjust = Object.assign({ chest: 0, waist: 0, hip: 0 }, this.profile.adjust || {});
      this.tone = this.profile.tone || 0;
    }

    var figureStrings = { loading: this.t('figureLoading'), error: this.t('figureError') };
    var settle = this.settleTrigger.bind(this);
    function fig(name, view) {
      return new Figure(qs('[data-fr-figure="' + name + '"]', dialog), { view: view, strings: figureStrings, onSettled: settle, waistY: bottom ? BELT_Y : 0 });
    }
    var bottom = this.type === 'bottom';
    this.figures = {
      full: fig('full', bottom ? 'fullBottom' : 'full'),
      mobile: fig('mobile', bottom ? 'fullBottom' : 'full'),
      result: fig('result', bottom ? 'resultBottom' : 'result'),
      measure: fig('measure', 'result'),
      measureMobile: fig('measureMobile', 'result')
    };

    this.bind();
    prewarm(this.trigger);
    this.sizeChart = new SizeChart(this);
    this.applyUnits(this.units, false);
    this.fillInputs();
    this.syncTone();
    if (this.profile) this.computeResult();
    this.updateTrigger();
  }

  /* Body measurements for sizing. Bottoms are measured at the belt line, which sits lower than
     the natural waist and measures a few cm more. */
  FittingRoom.prototype.bodyOf = function (profile) {
    var body = estimate(profile);
    if (this.type === 'bottom') body.waist += BELT_OFFSET;
    return body;
  };

  FittingRoom.prototype.t = function (key, map) {
    return fill(this.strings[key] || '', map || {});
  };

  FittingRoom.prototype.bind = function () {
    var self = this; var d = this.dialog;

    this.trigger.addEventListener('click', function () { self.open(); });
    qsa('[data-fr-close]', d).forEach(function (b) { b.addEventListener('click', function () { self.close(); }); });
    d.addEventListener('click', function (event) { if (event.target === d) self.close(); });
    d.addEventListener('close', function () { self.onClosed(); });
    d.addEventListener('cancel', function (event) {
      if (!self.panelOpen) return;
      event.preventDefault(); self.togglePanel(false);
    });

    qsa('[data-fr-units]', d).forEach(function (b) {
      b.addEventListener('click', function () { self.applyUnits(b.getAttribute('data-fr-units'), true); });
    });
    qsa('[data-fr-input]', d).forEach(function (input) {
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') { event.preventDefault(); self.goShape(); }
      });
      input.addEventListener('input', function () {
        var field = input.closest('[data-fr-field]');
        if (field) self.setError(field.getAttribute('data-fr-field'), '');
      });
    });
    qs('[data-fr-next]', d).addEventListener('click', function () { self.goShape(); });
    qs('[data-fr-back]', d).addEventListener('click', function () { self.show('details'); });
    qs('[data-fr-calc]', d).addEventListener('click', function () { self.calculate(); });
    qs('[data-fr-edit]', d).addEventListener('click', function () { self.show('measure'); });
    qs('[data-fr-reset]', d).addEventListener('click', function () { self.reset(); });

    qsa('[data-fr-slider]', d).forEach(function (row) {
      var area = row.getAttribute('data-fr-slider');
      var range = qs('[data-fr-range]', row);
      function set(v) {
        v = clamp(Math.round(v), -2, 2);
        self.adjust[area] = v * STEP_CM[area];
        self.syncSliders();
        self.scheduleFigures('shape');
      }
      range.addEventListener('input', function () { set(parseFloat(range.value)); });
      qs('[data-fr-dec]', row).addEventListener('click', function () { set(self.stepOf(area) - 1); });
      qs('[data-fr-inc]', row).addEventListener('click', function () { set(self.stepOf(area) + 1); });
    });

    qsa('[data-fr-meas]', d).forEach(function (row) {
      var scope = row.closest('[data-fr-meas-list]').getAttribute('data-fr-meas-list');
      var area = row.getAttribute('data-fr-meas');
      var range = qs('[data-fr-meas-range]', row);
      var input = qs('[data-fr-meas-input]', row);
      range.addEventListener('input', function () { self.setMeasure(area, parseFloat(range.value), scope, null); });
      input.addEventListener('change', function () {
        var v = parseNumber(input.value);
        if (isNaN(v)) { self.syncMeasures(); return; }
        self.setMeasure(area, self.units === 'imperial' ? v * 2.54 : v, scope, null);
      });
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') { event.preventDefault(); input.blur(); input.focus(); }
      });
      input.addEventListener('focus', function () { window.setTimeout(function () { input.select(); }, 0); });
      if (scope === 'measure') {
        row.addEventListener('focusin', function () { self.setFocusArea(area); });
        row.addEventListener('pointerenter', function () { self.setFocusArea(area); });
      }
    });
    qs('[data-fr-more]', d).addEventListener('click', function () {
      var button = this;
      var open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      button.textContent = open ? self.t('seeLess') : self.t('seeMore');
      qsa('[data-fr-more-row]', d).forEach(function (r) { r.hidden = !open; });
      if (!open && self.focusArea === 'hip') self.setFocusArea('chest');
    });
    qs('[data-fr-restart]', d).addEventListener('click', function () { self.show('details'); });
    qs('[data-fr-view]', d).addEventListener('click', function () { self.calculate(); });

    qsa('[data-fr-tone]', d).forEach(function (b) {
      b.addEventListener('click', function () {
        self.tone = parseInt(b.getAttribute('data-fr-tone'), 10) || 0;
        self.syncTone();
      });
    });
    var toneGroup = qs('.fr__tones', d);
    if (toneGroup) {
      toneGroup.addEventListener('keydown', function (event) {
        var tones = qsa('[data-fr-tone]', d);
        var dir = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        event.preventDefault();
        self.tone = (self.tone + dir + tones.length) % tones.length;
        self.syncTone();
        tones[self.tone].focus();
      });
    }

    qsa('[data-fr-panel-open]', d).forEach(function (b) { b.addEventListener('click', function () { self.togglePanel(true); }); });
    qs('[data-fr-panel-close]', d).addEventListener('click', function () { self.togglePanel(false); });

    var chartButton = qs('[data-fr-size-chart]', d);
    var chartTrigger = qs('[data-sc-dialog]', this.root) ? null : this.findSizeChart();
    if (qs('[data-sc-dialog]', this.root) || chartTrigger) {
      chartButton.hidden = false;
      chartButton.addEventListener('click', function () {
        self.close();
        window.setTimeout(function () {
          if (self.sizeChart && self.sizeChart.ok) self.sizeChart.open(); else chartTrigger.click();
        }, 60);
      });
    }

    qs('[data-fr-apply]', d).addEventListener('click', function () { self.applySize(); });

    var chips = qs('[data-fr-chips]', d);
    chips.addEventListener('keydown', function (event) {
      if (!self.result) return;
      var dir = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
      if (!dir) return;
      event.preventDefault();
      var n = self.result.results.length;
      self.select((self.selected + dir + n) % n);
      var active = qs('[aria-checked="true"]', chips);
      if (active) active.focus();
    });
  };

  /* ----- Open / close ----- */

  /* The trigger stays disabled from the click until the fitting room is ready to use */
  FittingRoom.prototype.setTriggerBusy = function (busy) {
    this.trigger.disabled = !!busy;
    this.trigger.classList.toggle('is-busy', !!busy);
    if (busy) this.trigger.setAttribute('aria-busy', 'true'); else this.trigger.removeAttribute('aria-busy');
  };

  FittingRoom.prototype.settleTrigger = function () {
    if (!this.trigger.disabled) return;
    var self = this;
    var waiting = Object.keys(this.figures || {}).some(function (k) {
      var f = self.figures[k];
      return f.pending && f.holder.offsetParent !== null;
    });
    if (!waiting) this.setTriggerBusy(false);
  };

  FittingRoom.prototype.open = function () {
    if (this.dialog.open || this.trigger.disabled) return;
    this.setTriggerBusy(true);
    this.lastFocus = document.activeElement;
    document.documentElement.classList.add('fr-locked');
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    warmOnOpen();
    this.show(this.profile && this.result ? 'result' : 'details', true);
    /* Nothing to load on the details step: release the trigger once the dialog has painted */
    var self = this;
    window.requestAnimationFrame(function () { window.requestAnimationFrame(function () { self.settleTrigger(); }); });
  };

  FittingRoom.prototype.close = function () {
    if (!this.dialog.open) return;
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else { this.dialog.removeAttribute('open'); this.onClosed(); }
  };

  FittingRoom.prototype.onClosed = function () {
    document.documentElement.classList.remove('fr-locked');
    this.setTriggerBusy(false);
    window.clearTimeout(this.loadingTimer);
    if (this.step === 'loading') this.step = 'result';
    this.togglePanel(false, true);
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
  };

  /* ----- Steps ----- */

  FittingRoom.prototype.show = function (step, initial) {
    var d = this.dialog;
    this.step = step;
    this.frame.setAttribute('data-step', step);
    qsa('[data-fr-step]', d).forEach(function (s) { s.hidden = s.getAttribute('data-fr-step') !== step; });
    qs('[data-fr-stage]', d).hidden = step !== 'shape';
    qs('[data-fr-measure-stage]', d).hidden = step !== 'measure';
    qs('[data-fr-photo]', d).hidden = step === 'shape' || step === 'measure';
    if (step === 'shape') this.syncSliders();
    if (step === 'measure') this.syncMeasures();
    qs('.fr__media [data-fr-panel-open]', d).hidden = step !== 'result';
    if (step !== 'result') this.togglePanel(false, true);
    if (step === 'shape') this.renderShapeFigure(false);
    if (step === 'measure') this.setFocusArea(this.focusArea);
    if (step === 'result') this.renderResult();
    var main = qs('.fr__main', d);
    if (main) main.scrollTop = 0;
    var heading = step === 'result' ? qs('[data-fr-verdict]', d) : qs('[data-fr-step="' + step + '"] .fr__title', d);
    if (step === 'details' && initial) {
      var first = qsa('[data-fr-input]', d).filter(function (i) { return i.offsetParent !== null; })[0];
      if (first && !first.value) { first.focus(); return; }
    }
    if (heading) heading.focus({ preventScroll: true });
  };

  FittingRoom.prototype.goShape = function () {
    var values = this.readInputs();
    if (!values) return;
    this.profile = Object.assign({}, this.profile || {}, values);
    this.show('shape');
  };

  FittingRoom.prototype.calculate = function () {
    var self = this;
    this.profile.adjust = Object.assign({}, this.adjust);
    this.profile.tone = this.tone;
    this.profile.units = this.units;
    storageSet(this.profile);
    this.computeResult();
    this.show('loading');
    this.loadingTimer = window.setTimeout(function () {
      self.show('result');
      self.updateTrigger();
    }, reducedMotion ? 150 : 550);
  };

  FittingRoom.prototype.reset = function () {
    storageSet(null);
    this.profile = null;
    this.result = null;
    this.adjust = { chest: 0, waist: 0, hip: 0 };
    this.tone = 0;
    qsa('[data-fr-input]', this.dialog).forEach(function (i) { i.value = ''; });
    this.syncSliders();
    this.syncTone();
    this.updateTrigger();
    this.show('details', true);
  };

  /* ----- Units and inputs ----- */

  FittingRoom.prototype.applyUnits = function (units, convert) {
    var d = this.dialog;
    var previous = this.units;
    var values = convert ? this.readRaw(previous) : null;
    this.units = units === 'imperial' ? 'imperial' : 'metric';
    qsa('[data-fr-units]', d).forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-fr-units') === this.units ? 'true' : 'false');
    }, this);
    var imperial = this.units === 'imperial';
    qs('[data-fr-metric]', d).hidden = imperial;
    qs('[data-fr-imperial]', d).hidden = !imperial;
    qs('[data-fr-weight-unit]', d).textContent = imperial ? this.t('lb') : this.t('kg');
    ['height', 'weight', 'age'].forEach(function (f) { this.setError(f, ''); }, this);
    if (convert && values && previous !== this.units) this.writeInputs(values);
    this.syncMeasures();
  };

  FittingRoom.prototype.input = function (name) { return qs('[data-fr-input="' + name + '"]', this.dialog); };

  /* Read whatever is typed, in metric, without validating (used when switching units). */
  FittingRoom.prototype.readRaw = function (units) {
    var out = {};
    if (units === 'imperial') {
      var ft = parseNumber(this.input('height_ft').value);
      var inch = parseNumber(this.input('height_in').value) || 0;
      if (!isNaN(ft)) out.height = (ft * 12 + inch) * 2.54;
      var lb = parseNumber(this.input('weight').value);
      if (!isNaN(lb)) out.weight = lb / 2.20462;
    } else {
      var cm = parseNumber(this.input('height_cm').value);
      if (!isNaN(cm)) out.height = cm;
      var kg = parseNumber(this.input('weight').value);
      if (!isNaN(kg)) out.weight = kg;
    }
    return out;
  };

  FittingRoom.prototype.writeInputs = function (values) {
    if (values.height) {
      this.input('height_cm').value = String(Math.round(values.height));
      var totalIn = Math.round(values.height / 2.54);
      this.input('height_ft').value = String(Math.floor(totalIn / 12));
      this.input('height_in').value = String(totalIn % 12);
    }
    if (values.weight) {
      this.input('weight').value = String(Math.round(this.units === 'imperial' ? values.weight * 2.20462 : values.weight));
    }
  };

  FittingRoom.prototype.fillInputs = function () {
    if (!this.profile) return;
    this.writeInputs(this.profile);
    this.input('age').value = String(this.profile.age);
  };

  FittingRoom.prototype.setError = function (field, message) {
    var el = qs('[data-fr-error="' + field + '"]', this.dialog);
    var wrap = qs('[data-fr-field="' + field + '"]', this.dialog);
    if (!el || !wrap) return;
    el.textContent = message;
    el.hidden = !message;
    qsa('input', wrap).forEach(function (i) {
      if (message) i.setAttribute('aria-invalid', 'true'); else i.removeAttribute('aria-invalid');
    });
  };

  FittingRoom.prototype.readInputs = function () {
    var imperial = this.units === 'imperial';
    var raw = this.readRaw(this.units);
    var age = parseNumber(this.input('age').value);
    var ok = true; var firstBad = null;
    function ftin(cm) { var t = Math.round(cm / 2.54); return Math.floor(t / 12) + "'" + (t % 12) + '"'; }

    if (!raw.height || raw.height < LIMITS.height[0] || raw.height > LIMITS.height[1]) {
      ok = false; firstBad = firstBad || (imperial ? 'height_ft' : 'height_cm');
      this.setError('height', this.t('errorHeight', imperial
        ? { min: ftin(LIMITS.height[0]), max: ftin(LIMITS.height[1]) }
        : { min: LIMITS.height[0] + ' cm', max: LIMITS.height[1] + ' cm' }));
    }
    if (!raw.weight || raw.weight < LIMITS.weight[0] || raw.weight > LIMITS.weight[1]) {
      ok = false; firstBad = firstBad || 'weight';
      this.setError('weight', this.t('errorWeight', imperial
        ? { min: Math.round(LIMITS.weight[0] * 2.20462) + ' lb', max: Math.round(LIMITS.weight[1] * 2.20462) + ' lb' }
        : { min: LIMITS.weight[0] + ' kg', max: LIMITS.weight[1] + ' kg' }));
    }
    if (isNaN(age) || age < LIMITS.age[0] || age > LIMITS.age[1]) {
      ok = false; firstBad = firstBad || 'age';
      this.setError('age', this.t('errorAge', { min: LIMITS.age[0], max: LIMITS.age[1] }));
    }
    if (!ok) { this.input(firstBad).focus(); return null; }
    return { height: Math.round(raw.height * 10) / 10, weight: Math.round(raw.weight * 10) / 10, age: Math.round(age) };
  };

  /* ----- Shape step ----- */

  /* Current shape step (-2..2) for an area; exact cm typed in "Edit measurements" round to the nearest step */
  FittingRoom.prototype.stepOf = function (area) {
    return clamp(Math.round((this.adjust[area] || 0) / STEP_CM[area]), -2, 2);
  };

  FittingRoom.prototype.syncSliders = function () {
    var self = this;
    qsa('[data-fr-slider]', this.dialog).forEach(function (row) {
      var area = row.getAttribute('data-fr-slider');
      var v = self.stepOf(area);
      var range = qs('[data-fr-range]', row);
      range.value = String(v);
      var len = LENGTH_AREAS[area];
      range.setAttribute('aria-valuetext', v === 0 ? self.t('asEstimated') : v < 0 ? self.t(len ? 'shorterBy' : 'smallerBy', { n: -v }) : self.t(len ? 'longerBy' : 'largerBy', { n: v }));
      range.style.setProperty('--fill', '0');
      qs('[data-fr-dec]', row).disabled = v <= -2;
      qs('[data-fr-inc]', row).disabled = v >= 2;
    });
  };

  FittingRoom.prototype.baseBody = function () {
    return this.bodyOf(Object.assign({}, this.profile, { adjust: {} }));
  };

  FittingRoom.prototype.formatMeasure = function (cm) {
    var v = this.units === 'imperial' ? cm / 2.54 : cm;
    v = Math.round(v * 2) / 2;
    return (v % 1 === 0 ? String(v) : v.toFixed(1));
  };

  FittingRoom.prototype.measureRange = function (area, scope, value) {
    var base = this.baseBody()[area];
    var min = scope === 'shape' ? base - SHAPE_RANGE : MEAS_LIMITS[area][0];
    var max = scope === 'shape' ? base + SHAPE_RANGE : MEAS_LIMITS[area][1];
    return [Math.min(min, value), Math.max(max, value)];
  };

  /* Set one body measurement (cm); keeps every slider, number box and figure in step. */
  FittingRoom.prototype.setMeasure = function (area, cm, scope, skipInput) {
    if (!this.profile || isNaN(cm)) return;
    var limits = MEAS_LIMITS[area];
    cm = clamp(cm, limits[0], limits[1]);
    this.adjust[area] = Math.round((cm - this.baseBody()[area]) * 2) / 2;
    this.syncMeasures(skipInput);
    this.scheduleFigures(scope);
  };

  FittingRoom.prototype.syncMeasures = function (skipInput) {
    if (!this.profile) return;
    var self = this;
    var base = this.baseBody();
    var unit = this.units === 'imperial' ? this.t('in') : this.t('cm');
    qsa('[data-fr-meas]', this.dialog).forEach(function (row) {
      var scope = row.closest('[data-fr-meas-list]').getAttribute('data-fr-meas-list');
      var area = row.getAttribute('data-fr-meas');
      var value = base[area] + (self.adjust[area] || 0);
      var range = qs('[data-fr-meas-range]', row);
      var input = qs('[data-fr-meas-input]', row);
      var lim = self.measureRange(area, scope, value);
      range.min = String(lim[0]); range.max = String(lim[1]); range.step = '0.5';
      range.value = String(value);
      range.setAttribute('aria-valuetext', self.formatMeasure(value) + ' ' + unit);
      if (input !== skipInput) input.value = self.formatMeasure(value);
      qs('[data-fr-meas-unit]', row).textContent = unit;
      var pct = (value - lim[0]) / (lim[1] - lim[0]) * 100;
      range.style.setProperty('--fill', (pct / 100).toFixed(3));
    });
  };

  /* Redraw at most once per frame while a slider is being dragged. */
  FittingRoom.prototype.scheduleFigures = function (scope) {
    var self = this;
    if (this.figureFrame) return;
    this.figureFrame = window.requestAnimationFrame(function () {
      self.figureFrame = null;
      if (scope === 'measure') self.renderMeasureFigure(null, true); else self.renderShapeFigure(false, true);
    });
    window.clearTimeout(this.figureSettle);
    this.figureSettle = window.setTimeout(function () {
      if (scope === 'measure') self.renderMeasureFigure(); else self.renderShapeFigure(false);
    }, 160);
  };

  var FOCUS = {
    chest: { view: 'chest', band: true }, waist: { view: 'waist', band: true }, hip: { view: 'hip', band: true },
    neck: { view: 'neck', band: true }, arm: { view: 'arm', overlay: 'arm' }, inseam: { view: 'inseam', overlay: 'inseam' },
    wrist: { view: 'wrist', wrist: true }
  };

  FittingRoom.prototype.setFocusArea = function (area) {
    var d = this.dialog;
    var changed = this.focusArea !== area;
    this.focusArea = area;
    qsa('[data-fr-meas-list="measure"] [data-fr-meas]', d).forEach(function (row) {
      row.classList.toggle('is-active', row.getAttribute('data-fr-meas') === area);
    });
    var text = (this.strings.how && this.strings.how[area]) || '';
    qsa('[data-fr-how]', d).forEach(function (p) { p.textContent = text; });
    this.renderMeasureFigure(changed ? 'zoom' : 'jump');
  };

  /* zoom: glide the camera to the area; jump: frame it at once; anything else: keep the camera */
  FittingRoom.prototype.renderMeasureFigure = function (camera, draft) {
    if (!this.profile) return;
    var P = figureParams(this.currentProfile(), visualBody(this.currentProfile()));
    var f = FOCUS[this.focusArea] || FOCUS.chest;
    var self = this;
    ['measure', 'measureMobile'].forEach(function (k) {
      var fig = self.figures[k];
      fig.setBands(f.band ? [{ area: self.focusArea, level: 'focus' }] : [], f.overlay, f.wrist);
      if (camera === 'jump' || camera === 'zoom' || !fig.params) {
        if (!fig.params) { fig.params = P; }
        var target = f.view === 'wrist' ? (fig.params = P, fig.wristView()) : VIEWS[f.view];
        fig.setView(target, camera === 'zoom');
      }
      fig.update(P, false, draft);
    });
  };

  FittingRoom.prototype.syncTone = function () {
    var self = this;
    var buttons = qsa('[data-fr-tone]', this.dialog);
    var color = '#EEEEEC';
    if (buttons.length) {
      if (self.tone >= buttons.length) self.tone = 0;
      color = buttons[self.tone].getAttribute('data-color');
      buttons.forEach(function (b, i) {
        b.setAttribute('aria-checked', i === self.tone ? 'true' : 'false');
        b.tabIndex = i === self.tone ? 0 : -1;
      });
    }
    Object.keys(this.figures).forEach(function (k) { self.figures[k].setTone(color); });
    if (this.profile) { this.profile.tone = this.tone; if (this.result) storageSet(this.profile); }
  };

  FittingRoom.prototype.currentProfile = function () {
    return Object.assign({}, this.profile, { adjust: Object.assign({}, this.adjust) });
  };

  FittingRoom.prototype.renderShapeFigure = function (animate, draft) {
    if (!this.profile) return;
    var profile = this.currentProfile();
    var P = figureParams(profile, visualBody(profile));
    /* bottoms: the leg-length arrow shows what the inseam step refers to */
    var overlay = this.type === 'bottom' ? 'inseam' : null;
    this.figures.full.setBands([], overlay);
    this.figures.mobile.setBands([], overlay);
    this.figures.full.update(P, animate, draft);
    this.figures.mobile.update(P, animate, draft);
  };

  /* ----- Result ----- */

  FittingRoom.prototype.computeResult = function () {
    var profile = this.currentProfile();
    this.body = this.bodyOf(profile);
    this.result = recommend(this.body, this.table);
    this.selected = this.result.best;
  };

  /* What the UI shows for a size: the top pick reads as a clean fit everywhere; every other
     size keeps its real level and per-area flags so the shopper sees why it isn't recommended */
  FittingRoom.prototype.display = function (r) {
    if (!r.isTopPick) return { level: r.level, areas: r.areas };
    var areas = {};
    Object.keys(r.areas).forEach(function (a) { areas[a] = { key: 'ideal', level: 'ok' }; });
    return { level: 'ok', areas: areas };
  };

  FittingRoom.prototype.verdictFor = function (index) {
    var r = this.result.results[index];
    if (r.isTopPick) return 'top';
    if (index === this.result.best) return r.level === 'ok' ? 'best' : 'closest';
    return r.level === 'bad' ? 'not' : 'also';
  };

  FittingRoom.prototype.renderResult = function () {
    if (!this.result) this.computeResult();
    this.renderChips();
    this.select(this.selected, true);
    qs('[data-fr-note]', this.dialog).hidden = !this.result.outOfRange;
  };

  FittingRoom.prototype.renderChips = function () {
    var self = this;
    var wrap = qs('[data-fr-chips]', this.dialog);
    while (wrap.firstChild) wrap.removeChild(wrap.firstChild);
    var statusText = { ok: this.t('statusOk'), warn: this.t('statusWarn'), bad: this.t('statusBad') };
    this.result.results.forEach(function (raw, i) {
      var r = { size: raw.size, level: self.display(raw).level };
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'fr-chip fr-chip--' + r.level;
      chip.setAttribute('role', 'radio');
      chip.setAttribute('data-index', String(i));
      var name = document.createElement('span');
      name.className = 'fr-chip__name';
      name.textContent = r.size.name;
      chip.appendChild(name);
      if (r.level !== 'warn') {
        var badge = document.createElement('span');
        badge.className = 'fr-badge fr-badge--' + r.level;
        badge.setAttribute('aria-hidden', 'true');
        chip.appendChild(badge);
      }
      var sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = ', ' + statusText[r.level];
      chip.appendChild(sr);
      chip.addEventListener('click', function () { self.select(i); });
      wrap.appendChild(chip);
    });
  };

  FittingRoom.prototype.select = function (index, initial) {
    var self = this; var d = this.dialog;
    this.selected = index;
    var raw = this.result.results[index];
    var shown = this.display(raw);
    var r = { size: raw.size, level: shown.level, areas: shown.areas };
    var verdict = this.verdictFor(index);

    qsa('[data-fr-chips] .fr-chip', d).forEach(function (chip, i) {
      chip.setAttribute('aria-checked', i === index ? 'true' : 'false');
      chip.tabIndex = i === index ? 0 : -1;
    });
    if (!initial) {
      var activeChip = qs('[data-fr-chips] [aria-checked="true"]', d);
      if (activeChip && activeChip.scrollIntoView) activeChip.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    qs('[data-fr-verdict]', d).textContent = this.t(verdict);
    qs('[data-fr-size-name]', d).textContent = r.size.name;
    var card = qs('[data-fr-card]', d);
    card.className = 'fr-result__card fr-result__card--' + r.level;
    var cardBadge = qs('[data-fr-card-badge]', d);
    cardBadge.className = 'fr-badge fr-badge--' + r.level;
    cardBadge.hidden = r.level === 'warn';

    /* Figure: always the shopper's body; the tape colours follow the selected size. */
    var figure = this.figures.result;
    var P = figureParams(this.currentProfile(), visualBody(this.currentProfile()));
    var bands = this.table.areas.filter(function (a) { return r.areas[a] && BAND_AREAS.indexOf(a) !== -1; }).map(function (a) {
      return { area: a, level: r.areas[a].level };
    });
    var showInseam = this.type === 'bottom' && r.areas.inseam;
    figure.setBands(bands, showInseam ? 'inseam' : null);
    figure.update(P, !initial);
    if (showInseam) bands = bands.concat([{ area: 'inseam', level: r.areas.inseam.level, top: figure.yTop(0.25) }]);

    /* Label heights: next to their tape, but never closer than 13% of the figure apart */
    var labelTops = {};
    bands.map(function (b) { return { area: b.area, top: b.top !== undefined ? b.top : figure.bandTop(b.area) }; })
      .sort(function (x, y) { return x.top - y.top; })
      .forEach(function (b, i, list) {
        if (i > 0) b.top = Math.max(b.top, list[i - 1].top + 0.13);
        labelTops[b.area] = b.top;
      });
    var labels = qs('[data-fr-labels]', d);
    while (labels.firstChild) labels.removeChild(labels.firstChild);
    bands.forEach(function (band) {
      var rate = r.areas[band.area];
      var li = document.createElement('li');
      li.className = 'fr-label fr-label--' + rate.level;
      li.style.top = (labelTops[band.area] * 100).toFixed(2) + '%';
      li.appendChild(fitIcon(rate.key, rate.level));
      var text = document.createElement('span');
      text.className = 'fr-label__text';
      var area = document.createElement('span');
      area.className = 'fr-label__area';
      area.textContent = self.t(band.area);
      var fit = document.createElement('span');
      fit.className = 'fr-label__fit';
      var fitKey = LENGTH_AREAS[band.area] && LENGTH_KEYS[rate.key] ? LENGTH_KEYS[rate.key] : rate.key;
      fit.textContent = (self.strings.fit && self.strings.fit[fitKey]) || fitKey;
      text.appendChild(area);
      text.appendChild(fit);
      li.appendChild(text);
      labels.appendChild(li);
    });

    this.renderPanel(raw);
    this.renderApply(r);
  };

  FittingRoom.prototype.renderPanel = function (r) {
    var self = this; var d = this.dialog;
    qs('[data-fr-panel-size-head]', d).textContent = this.t('sizeHead', { size: r.size.name });
    var body = qs('[data-fr-panel-rows]', d);
    while (body.firstChild) body.removeChild(body.firstChild);
    this.table.areas.forEach(function (area) {
      if (!r.size.ranges[area]) return;
      var tr = document.createElement('tr');
      var th = document.createElement('th');
      th.scope = 'row';
      th.textContent = self.t(area);
      var you = document.createElement('td');
      you.textContent = Math.round(self.body[area]) + ' cm';
      var range = document.createElement('td');
      range.textContent = fmt(r.size.ranges[area][0]) + '\u2013' + fmt(r.size.ranges[area][1]) + ' cm';
      tr.appendChild(th); tr.appendChild(you); tr.appendChild(range);
      body.appendChild(tr);
    });
  };

  FittingRoom.prototype.togglePanel = function (open, silent) {
    var panel = qs('[data-fr-panel]', this.dialog);
    this.panelOpen = !!open;
    panel.hidden = !open;
    this.frame.classList.toggle('is-panel-open', !!open);
    if (silent) return;
    if (open) qs('[data-fr-panel-title]', this.dialog).focus({ preventScroll: true });
    else {
      var back = qsa('[data-fr-panel-open]', this.dialog).filter(function (b) { return b.offsetParent !== null; })[0];
      if (back) back.focus({ preventScroll: true });
    }
  };

  /* ----- Variant picker hook ----- */

  function normalizeSize(v) {
    var s = String(v || '').toUpperCase().replace(/\s+/g, '');
    var map = { XXL: '2XL', XXXL: '3XL', XXXXL: '4XL', XXS: '2XS' };
    return map[s] || s;
  }

  FittingRoom.prototype.sizeInputs = function () {
    var pattern = /size|taille|gr(\u00f6|oe)(\u00df|ss)e|talla|tamanho|taglia|maat/i;
    var match = null;
    qsa('[data-option-index]', this.product).forEach(function (fs) {
      if (match) return;
      var name = qs('.option__name', fs);
      if (name && pattern.test(name.textContent)) match = fs;
    });
    return match ? qsa('input[data-option-input]', match) : [];
  };

  FittingRoom.prototype.findSizeInput = function (sizeName) {
    var target = normalizeSize(sizeName);
    return this.sizeInputs().filter(function (i) { return normalizeSize(i.value) === target; })[0] || null;
  };

  FittingRoom.prototype.renderApply = function (r) {
    var button = qs('[data-fr-apply]', this.dialog);
    var input = this.findSizeInput(r.size.name);
    button.hidden = !input;
    button.textContent = this.t('apply', { size: r.size.name });
  };

  FittingRoom.prototype.applySize = function () {
    var r = this.result.results[this.selected];
    var input = this.findSizeInput(r.size.name);
    if (!input) return;
    if (!input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.close();
    var label = input.id ? qs('label[for="' + CSS.escape(input.id) + '"]', this.product) : null;
    if (label && label.focus) label.focus({ preventScroll: true });
  };

  FittingRoom.prototype.findSizeChart = function () {
    var selectors = [
      '[data-size-chart-open]', '[data-size-chart-trigger]', '[data-open-size-chart]',
      '[aria-controls*="size-chart" i]', '[aria-controls*="SizeChart"]', '.option__guide'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = null;
      try { el = qs(selectors[i], this.product); } catch (e) { el = null; }
      if (el && !this.root.contains(el)) return el;
    }
    return null;
  };

  FittingRoom.prototype.updateTrigger = function () {
    if (this.profile && this.result) {
      var best = this.result.results[this.result.best];
      this.triggerLabel.textContent = this.t('buttonResult', { size: best.size.name });
      this.trigger.classList.add('has-result');
    } else {
      this.triggerLabel.textContent = this.triggerDefault;
      this.trigger.classList.remove('has-result');
    }
  };

  /* ---------- Size chart ----------
     Body ranges per size from the same table the fitting room uses. Hovering (or focusing)
     a measurement swaps the product photo for a line drawing showing where to measure. */
  function SizeChart(fr) {
    var dialog = qs('[data-sc-dialog]', fr.root);
    this.ok = !!dialog;
    if (!dialog) return;
    var self = this;
    qsa('body > dialog#' + CSS.escape(dialog.id)).forEach(function (old) { if (old !== dialog) old.remove(); });
    document.body.appendChild(dialog);
    this.fr = fr;
    this.dialog = dialog;
    this.trigger = qs('[data-size-chart-open]', fr.root);
    this.chips = qs('[data-sc-chips]', dialog);
    this.rows = qs('[data-sc-rows]', dialog);
    this.photo = qs('[data-sc-photo]', dialog);
    this.diagram = qs('[data-sc-diagram]', dialog);
    this.how = qs('[data-sc-how]', dialog);
    this.hasPhoto = !!qs('img', this.photo);
    this.index = 0;

    if (this.trigger) this.trigger.addEventListener('click', function () { self.open(); });
    qs('[data-sc-close]', dialog).addEventListener('click', function () { self.close(); });
    dialog.addEventListener('click', function (e) { if (e.target === dialog) self.close(); });
    dialog.addEventListener('close', function () {
      document.documentElement.classList.remove('fr-locked');
      if (self.lastFocus && self.lastFocus.focus && !self.handoff) self.lastFocus.focus();
      self.handoff = false;
    });
    qs('[data-sc-prev]', dialog).addEventListener('click', function () { self.page(-1); });
    qs('[data-sc-next]', dialog).addEventListener('click', function () { self.page(1); });
    this.chips.addEventListener('scroll', function () { self.updateArrows(); }, { passive: true });
    this.chips.addEventListener('keydown', function (event) {
      var dir = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
      if (!dir) return;
      event.preventDefault();
      self.select(clamp(self.index + dir, 0, fr.table.sizes.length - 1), true);
    });
    this.rows.addEventListener('mouseleave', function () { self.showPhoto(); });
    qs('[data-sc-fr]', dialog).addEventListener('click', function () {
      self.handoff = true;
      self.close();
      fr.open();
    });
  }

  SizeChart.prototype.units = function () {
    return this.fr.units === 'imperial' ? 'in' : 'cm';
  };

  SizeChart.prototype.format = function (range) {
    var u = this.units();
    function one(v) { return u === 'in' ? (Math.round(v / 2.54 * 2) / 2).toString() : fmt(v); }
    return one(range[0]) + ' - ' + one(range[1]) + ' ' + this.fr.t(u);
  };

  /* Opens on the shopper's recommended size, else the size picked on the page, else the first */
  SizeChart.prototype.defaultIndex = function () {
    var fr = this.fr; var sizes = fr.table.sizes;
    if (fr.profile && fr.result) return fr.result.best;
    var checked = fr.sizeInputs().filter(function (i) { return i.checked; })[0];
    if (checked) {
      for (var i = 0; i < sizes.length; i++) if (normalizeSize(sizes[i].name) === normalizeSize(checked.value)) return i;
    }
    return 0;
  };

  SizeChart.prototype.open = function () {
    if (this.dialog.open) return;
    this.lastFocus = document.activeElement;
    this.renderChips();
    this.select(this.defaultIndex(), false);
    this.showPhoto();
    document.documentElement.classList.add('fr-locked');
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal(); else this.dialog.setAttribute('open', '');
    var active = qs('[aria-checked="true"]', this.chips);
    this.scrollToChip(active, false);
    if (active) active.focus({ preventScroll: true });
  };

  SizeChart.prototype.close = function () {
    if (!this.dialog.open) return;
    if (typeof this.dialog.close === 'function') this.dialog.close(); else this.dialog.removeAttribute('open');
  };

  SizeChart.prototype.renderChips = function () {
    var self = this;
    while (this.chips.firstChild) this.chips.removeChild(this.chips.firstChild);
    this.fr.table.sizes.forEach(function (size, i) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'sc__chip';
      chip.setAttribute('role', 'radio');
      chip.textContent = size.name;
      chip.addEventListener('click', function () { self.select(i, true); });
      self.chips.appendChild(chip);
    });
  };

  SizeChart.prototype.select = function (index, focus) {
    var self = this;
    this.index = index;
    var size = this.fr.table.sizes[index];
    qsa('.sc__chip', this.chips).forEach(function (chip, i) {
      chip.setAttribute('aria-checked', i === index ? 'true' : 'false');
      chip.tabIndex = i === index ? 0 : -1;
      if (i === index && focus) { chip.focus({ preventScroll: true }); self.scrollToChip(chip, true); }
    });
    qs('[data-sc-caption]', this.dialog).textContent = this.fr.t('scCaption', { size: size.name });
    while (this.rows.firstChild) this.rows.removeChild(this.rows.firstChild);
    /* Waist first, then chest, as shoppers read them; any other columns follow */
    var order = ['waist', 'chest', 'hip', 'neck', 'arm', 'inseam', 'wrist'];
    order.filter(function (a) { return size.ranges[a]; }).forEach(function (area) {
      var tr = document.createElement('tr');
      tr.className = 'sc__row';
      tr.tabIndex = 0;
      tr.setAttribute('data-area', area);
      var th = document.createElement('th');
      th.scope = 'row';
      th.textContent = self.fr.t(area);
      var td = document.createElement('td');
      td.textContent = self.format(size.ranges[area]);
      tr.appendChild(th); tr.appendChild(td);
      tr.addEventListener('mouseenter', function () { self.showArea(area); });
      tr.addEventListener('focus', function () { self.showArea(area); });
      tr.addEventListener('click', function () { self.showArea(area); });
      self.rows.appendChild(tr);
    });
    this.updateArrows();
  };

  SizeChart.prototype.showArea = function (area) {
    var self = this;
    this.photo.hidden = true;
    this.diagram.hidden = false;
    qsa('[data-sc-arrow]', this.diagram).forEach(function (g) { g.style.display = g.getAttribute('data-sc-arrow') === area ? '' : 'none'; });
    this.how.textContent = (this.fr.strings.how && this.fr.strings.how[area]) || '';
    qsa('.sc__row', this.rows).forEach(function (r) { r.classList.toggle('is-active', r.getAttribute('data-area') === area); });
    this.area = area;
  };

  SizeChart.prototype.showPhoto = function () {
    this.area = null;
    this.photo.hidden = !this.hasPhoto;
    this.diagram.hidden = this.hasPhoto;
    qsa('[data-sc-arrow]', this.diagram).forEach(function (g) { g.style.display = 'none'; });
    this.how.textContent = '';
    qsa('.sc__row', this.rows).forEach(function (r) { r.classList.remove('is-active'); });
  };

  SizeChart.prototype.chipStep = function () {
    var chip = qs('.sc__chip', this.chips);
    return chip ? chip.offsetWidth + 16 : 120;
  };

  SizeChart.prototype.page = function (dir) {
    this.chips.scrollBy({ left: dir * this.chipStep(), behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  SizeChart.prototype.scrollToChip = function (chip, smooth) {
    if (!chip) return;
    var box = this.chips;
    /* Position of the chip inside the strip (not the dialog), centred where possible */
    var left = chip.getBoundingClientRect().left - box.getBoundingClientRect().left + box.scrollLeft;
    var target = clamp(left - (box.clientWidth - chip.offsetWidth) / 2, 0, box.scrollWidth - box.clientWidth);
    var self = this;
    box.scrollTo({ left: target, behavior: smooth && !reducedMotion ? 'smooth' : 'auto' });
    window.setTimeout(function () { self.updateArrows(); }, smooth && !reducedMotion ? 420 : 0);
    this.updateArrows();
  };

  SizeChart.prototype.updateArrows = function () {
    var box = this.chips;
    var max = box.scrollWidth - box.clientWidth;
    /* A few px of slack: scroll snapping can stop just short of the ends */
    qs('[data-sc-prev]', this.dialog).disabled = box.scrollLeft <= 4;
    qs('[data-sc-next]', this.dialog).disabled = box.scrollLeft >= max - 4;
  };

  /* ---------- Boot ---------- */

  function init(root) {
    if (root.__fittingRoom) return;
    root.__fittingRoom = new FittingRoom(root);
  }

  function initAll(scope) { qsa('[data-fitting-room]', scope).forEach(init); }

  initAll(document);

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:block:select', function (event) {
    var root = qs('[data-fitting-room][data-block-id="' + event.detail.blockId + '"]');
    if (root && root.__fittingRoom && root.__fittingRoom.dialog) root.__fittingRoom.open();
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    var root = qs('[data-fitting-room][data-block-id="' + event.detail.blockId + '"]');
    if (root && root.__fittingRoom && root.__fittingRoom.dialog) root.__fittingRoom.close();
  });

  /* Small hooks for tests and future tweaks. */
  window.SavileFittingRoom = { parseTable: parseTable, estimate: estimate, recommend: recommend };
})();
